// ============================================================
// SupervisorPage.tsx — صفحة المشرف / رئيس القسم / مدير المناوبة
// مُناوِب v2 — تطابق كامل مع النظام الأصلي
// ============================================================

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuthStore, useIncidents, useCreateIncident, useIncident, useIncidentAction, useSlaStatus } from '@munawb/hooks';
import { useNotifications, useMarkNotificationsRead } from '@munawb/hooks';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { createIncidentSchema, type CreateIncidentInput } from '@munawb/validators';
import { supabase } from '@munawb/supabase';
import type { IncidentRow } from '@munawb/types';
import { useQuery } from '@tanstack/react-query';
import { getInitials, formatDate } from '../lib/utils';

/* ── Types ───────────────────────────────────────────────────── */
type TabId = 'overview' | 'dept-events' | 'my-events' | 'rca-done' | 'new-event';
type Department   = { id: string; name: string };
type MainCategory = { name: string; subs: string[] };
type DeptEmployee = { id: string; name: string };
interface Toast { id: number; msg: string; type: 'success' | 'error' | 'info' }

/* ── Constants ───────────────────────────────────────────────── */
const PAGE_SIZE = 12;

const ROLE_LABELS: Record<string, string> = {
  Employee:'موظف', Supervisor:'مشرف', HeadOfDept:'رئيس قسم',
  DutyManager:'مدير مناوب', QualitySuper:'مشرف جودة',
  Manager:'مدير', Executive:'تنفيذي', Admin:'أدمن',
};

const STATUS_STYLE: Record<string, { bg: string; color: string; border: string }> = {
  'مفتوح':         { bg:'#eff6ff', color:'#2563eb', border:'#bfdbfe' },
  'مصعد':          { bg:'#fff7ed', color:'#ea580c', border:'#fed7aa' },
  'موجه':          { bg:'#f0f9ff', color:'#0284c7', border:'#bae6fd' },
  'طلب RCA':       { bg:'#faf5ff', color:'#9333ea', border:'#e9d5ff' },
  'مغلق':          { bg:'#f8fafc', color:'#64748b', border:'#e2e8f0' },
  'RCA Completed': { bg:'#f0fdf4', color:'#16a34a', border:'#bbf7d0' },
};

const RISK_BORDER: Record<string, string> = {
  'منخفض':'#10b981','متوسط':'#f59e0b','عالي':'#ea580c','مرتفع':'#ea580c','حرج':'#ef4444',
};
const RISK_GRADIENT: Record<string, string> = {
  'منخفض':'linear-gradient(135deg,#10b981,#059669)',
  'متوسط':'linear-gradient(135deg,#f59e0b,#d97706)',
  'عالي': 'linear-gradient(135deg,#ea580c,#c2410c)',
  'مرتفع':'linear-gradient(135deg,#ea580c,#c2410c)',
  'حرج':  'linear-gradient(135deg,#ef4444,#b91c1c)',
};
const LOG_STYLE: Record<string, { bg:string; color:string; icon:string }> = {
  update:      { bg:'#eff6ff', color:'#2563eb', icon:'🔄' },
  close:       { bg:'#f0fdf4', color:'#16a34a', icon:'✅' },
  escalate:    { bg:'#fff7ed', color:'#ea580c', icon:'🔺' },
  assign:      { bg:'#faf5ff', color:'#9333ea', icon:'👤' },
  direct:      { bg:'#f0f9ff', color:'#0284c7', icon:'➡️' },
  request_rca: { bg:'#fdf4ff', color:'#a21caf', icon:'🔬' },
  rca:         { bg:'#fdf4ff', color:'#a21caf', icon:'📋' },
  reopen:      { bg:'#fefce8', color:'#ca8a04', icon:'🔓' },
};

const DUTY_MANAGER_PLUS = ['DutyManager','QualitySuper','Manager','Executive','Admin'];

/* ══════════════════════════════════════════════════════════════
   MAIN PAGE
══════════════════════════════════════════════════════════════ */
export default function SupervisorPage() {
  const { profile } = useAuthStore();
  const isDutyMgrPlus = profile ? DUTY_MANAGER_PLUS.includes(profile.role) : false;

  const [activeTab, setActiveTab]     = useState<TabId>('overview');
  const [darkMode, setDarkMode]       = useState(() => localStorage.getItem('munawb_theme') === 'dark');
  const [toasts, setToasts]           = useState<Toast[]>([]);
  const [selectedId, setSelectedId]   = useState<string | null>(null);
  const [showNotif, setShowNotif]     = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showCall, setShowCall]       = useState(false);
  const [showLogout, setShowLogout]   = useState(false);
  const [rcaViewId, setRcaViewId]     = useState<string | null>(null);

  /* Batch mode */
  const [batchMode, setBatchMode]         = useState(false);
  const [selectedBatch, setSelectedBatch] = useState<Set<string>>(new Set());
  const [batchPending, setBatchPending]   = useState(false);

  /* Search / Filter / Page */
  const [deptSearch,  setDeptSearch]  = useState('');
  const [deptStatus,  setDeptStatus]  = useState('');
  const [deptPage,    setDeptPage]    = useState(1);
  const [mySearch,    setMySearch]    = useState('');
  const [myStatus,    setMyStatus]    = useState('');
  const [myPage,      setMyPage]      = useState(1);
  const [rcaSearch,   setRcaSearch]   = useState('');

  /* Dark mode */
  useEffect(() => {
    document.documentElement.classList.toggle('dark', darkMode);
    localStorage.setItem('munawb_theme', darkMode ? 'dark' : 'light');
  }, [darkMode]);

  const showToast = useCallback((msg: string, type: Toast['type'] = 'info') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, msg, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3500);
  }, []);

  /* ── Data: Dept incidents ── */
  const { data: deptIncidents = [], isLoading: loadingDept } = useIncidents({
    dept_id: isDutyMgrPlus ? undefined : (profile as any)?.dept_id ?? undefined,
    include_closed: true,
  });

  /* ── Data: My incidents ── */
  const { data: myIncidents = [], isLoading: loadingMy } = useIncidents({
    reporter_id: profile?.id,
    include_closed: true,
  });

  /* ── Notifications ── */
  const { data: notifications = [] } = useNotifications(profile?.id ?? '', null, true);
  const { mutate: markRead }          = useMarkNotificationsRead(profile?.id ?? '');
  const unreadCount = notifications.filter(n => !n.is_read).length;

  /* ── Departments ── */
  const { data: departments = [] } = useQuery<Department[]>({
    queryKey: ['departments'],
    queryFn: async () => {
      const { data } = await supabase.from('departments').select('id,name').eq('active', true).order('name');
      return data ?? [];
    },
  });

  /* ── Categories ── */
  const { data: categories = [] } = useQuery<MainCategory[]>({
    queryKey: ['event_categories'],
    queryFn: async () => {
      const { data } = await supabase.from('event_categories').select('main_cat,sub_cat').eq('active', true).order('main_cat');
      if (!data) return [];
      const map = new Map<string, string[]>();
      for (const row of data) {
        if (!map.has(row.main_cat)) map.set(row.main_cat, []);
        if (row.sub_cat) map.get(row.main_cat)!.push(row.sub_cat);
      }
      return Array.from(map.entries()).map(([name, subs]) => ({ name, subs }));
    },
  });

  /* ── Dept employees ── */
  const { data: deptEmployees = [] } = useQuery<DeptEmployee[]>({
    queryKey: ['dept_employees', (profile as any)?.dept_id],
    queryFn: async () => {
      const deptId = (profile as any)?.dept_id;
      if (!deptId) return [];
      const { data } = await supabase.from('profiles').select('id,name').eq('dept_id', deptId).eq('status', 'active');
      return data ?? [];
    },
    enabled: !!( profile as any)?.dept_id,
  });

  /* ── KPIs ── */
  const kpiOpen      = deptIncidents.filter(i => i.status === 'مفتوح').length;
  const kpiEscalated = deptIncidents.filter(i => i.status === 'مصعد').length;
  const kpiBreach    = deptIncidents.filter(i => {
    if (['مغلق','RCA Completed'].includes(i.status)) return false;
    const elapsed = (Date.now() - new Date(i.created_at).getTime()) / 60000;
    return elapsed > i.sla_minutes;
  }).length;

  /* ── Status distribution ── */
  const statusDist = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const inc of deptIncidents) counts[inc.status] = (counts[inc.status] ?? 0) + 1;
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  }, [deptIncidents]);

  const riskDist = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const inc of deptIncidents) counts[inc.risk_level] = (counts[inc.risk_level] ?? 0) + 1;
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  }, [deptIncidents]);

  /* ── Filtered lists ── */
  const filteredDept = useMemo(() => deptIncidents.filter(i => {
    const matchStatus = !deptStatus || i.status === deptStatus;
    const q = deptSearch.toLowerCase();
    const matchSearch = !q || i.description.toLowerCase().includes(q) || i.event_type.toLowerCase().includes(q) || i.location.toLowerCase().includes(q) || i.event_id.toLowerCase().includes(q);
    return matchStatus && matchSearch;
  }), [deptIncidents, deptStatus, deptSearch]);

  const filteredMy = useMemo(() => myIncidents.filter(i => {
    const matchStatus = !myStatus || i.status === myStatus;
    const q = mySearch.toLowerCase();
    const matchSearch = !q || i.description.toLowerCase().includes(q) || i.event_type.toLowerCase().includes(q) || i.location.toLowerCase().includes(q) || i.event_id.toLowerCase().includes(q);
    return matchStatus && matchSearch;
  }), [myIncidents, myStatus, mySearch]);

  const rcaDone = useMemo(() => deptIncidents.filter(i =>
    i.status === 'RCA Completed' && (
      !rcaSearch || i.description.toLowerCase().includes(rcaSearch.toLowerCase()) || i.event_id.toLowerCase().includes(rcaSearch.toLowerCase())
    )
  ), [deptIncidents, rcaSearch]);

  const slicedDept = filteredDept.slice((deptPage - 1) * PAGE_SIZE, deptPage * PAGE_SIZE);
  const slicedMy   = filteredMy.slice((myPage - 1) * PAGE_SIZE, myPage * PAGE_SIZE);

  const handleMarkAllRead = () => {
    const ids = notifications.filter(n => !n.is_read).map(n => n.id);
    if (ids.length) markRead(ids);
  };

  const handleSignOut = async () => {
    setShowLogout(false);
    localStorage.removeItem('munawb-profile');
    await supabase.auth.signOut();
  };

  const toggleBatch = (id: string) => {
    setSelectedBatch(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const executeBatch = async (action: 'close' | 'escalate') => {
    if (selectedBatch.size === 0) { showToast('لم تحدد أي حدث', 'error'); return; }
    setBatchPending(true);
    let done = 0;
    for (const incId of selectedBatch) {
      try {
        await supabase.from('incident_logs').insert({
          incident_id: incId,
          action_type: action,
          description: action === 'close' ? 'إغلاق جماعي بواسطة المشرف' : 'تصعيد جماعي بواسطة المشرف',
          actor_id: profile?.id ?? null,
          actor_name: profile?.name ?? 'المشرف',
        });
        await supabase.from('incidents').update({ status: action === 'close' ? 'مغلق' : 'مصعد', closed_at: action === 'close' ? new Date().toISOString() : null } as any).eq('id', incId);
        done++;
      } catch { /* ignore individual error */ }
    }
    setBatchPending(false);
    setBatchMode(false);
    setSelectedBatch(new Set());
    showToast(`تم تنفيذ الإجراء على ${done} حدث ✅`, 'success');
    window.location.reload();
  };

  const printList = (list: IncidentRow[], title: string) => {
    const rows = list.map(i => `
      <tr>
        <td dir="ltr" style="font-family:monospace">${i.event_id}</td>
        <td>${i.reporter_name}</td>
        <td>${i.location}</td>
        <td>${i.event_type}</td>
        <td>${i.risk_level}</td>
        <td>${i.status}</td>
        <td dir="ltr">${new Date(i.created_at).toLocaleDateString('en-GB')}</td>
      </tr>`).join('');
    const H = `<!DOCTYPE html><html dir="rtl" lang="ar"><head><meta charset="UTF-8">
      <title>${title}</title>
      <style>
        @import url('https://fonts.googleapis.com/css2?family=Tajawal:wght@400;700;900&display=swap');
        *{font-family:'Tajawal',sans-serif;box-sizing:border-box;}
        body{background:#fff;color:#0f172a;font-size:12px;padding:20px;}
        h1{font-size:18px;font-weight:900;text-align:center;margin-bottom:4px;}
        .sub{text-align:center;color:#64748b;font-size:11px;margin-bottom:20px;}
        table{width:100%;border-collapse:collapse;border:1px solid #e2e8f0;}
        th,td{padding:8px 10px;text-align:right;border:1px solid #e2e8f0;font-size:11px;}
        th{background:#0f172a;color:#fff;font-weight:900;}
        tr:nth-child(even){background:#f8fafc;}
        .footer{text-align:center;margin-top:16px;font-size:10px;color:#94a3b8;}
        @media print{@page{margin:1.5cm;size:A4 landscape;}}
      </style></head><body>
      <h1>نظام مُناوِب | ${title}</h1>
      <div class="sub">تاريخ الطباعة: ${new Date().toLocaleString('ar-SA')}</div>
      <table>
        <thead><tr><th>رقم الحدث</th><th>المُبلِّغ</th><th>القسم</th><th>النوع</th><th>الخطورة</th><th>الحالة</th><th>التاريخ</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>
      <div class="footer">إجمالي السجلات: ${list.length} — نظام مُناوِب للإدارة التشغيلية</div>
    </body></html>`;
    const w = window.open('', '_blank');
    if (w) { w.document.write(H); w.document.close(); setTimeout(() => { w.focus(); w.print(); }, 500); }
  };

  const bg = darkMode ? '#020617' : '#f1f5f9';

  return (
    <div dir="rtl" style={{ minHeight:'100vh', fontFamily:"'Tajawal',sans-serif", background:bg, color: darkMode ? '#f1f5f9' : '#0f172a' }}>

      {/* Toasts */}
      <ToastList toasts={toasts} />

      {/* ══ HEADER ══ */}
      <header style={{ background:'linear-gradient(135deg,#0f172a,#1e293b)', position:'sticky', top:0, zIndex:40, boxShadow:'0 4px 24px rgba(0,0,0,0.3)' }}>
        <div style={{ maxWidth:1400, margin:'0 auto', padding:'0 20px', height:64, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          {/* Logo */}
          <div style={{ display:'flex', alignItems:'center', gap:12 }}>
            <div style={{ width:38, height:38, background:'linear-gradient(135deg,#3b82f6,#2563eb)', borderRadius:10, display:'flex', alignItems:'center', justifyContent:'center', fontWeight:900, color:'#fff', fontSize:16 }}>م</div>
            <div>
              <div style={{ color:'#fff', fontWeight:900, fontSize:17 }}>مُناوِب</div>
              <div style={{ color:'#94a3b8', fontSize:10, fontWeight:700 }}>القيادة والإشراف</div>
            </div>
          </div>

          {/* Controls */}
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <div style={{ width:34, height:34, background:'linear-gradient(135deg,#f59e0b,#d97706)', borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontSize:13, fontWeight:900 }}>
                {getInitials(profile?.name ?? 'م')}
              </div>
              <div>
                <div style={{ color:'#fff', fontSize:13, fontWeight:800 }}>{profile?.name}</div>
                <div style={{ display:'inline-flex', background:'linear-gradient(135deg,#fef3c7,#fde68a)', color:'#92400e', borderRadius:20, padding:'1px 8px', fontSize:10, fontWeight:900 }}>
                  {ROLE_LABELS[profile?.role ?? ''] ?? profile?.role}
                </div>
              </div>
            </div>

            {/* Call button */}
            <HdrBtn onClick={() => setShowCall(true)} title="نظام الاستدعاء الطارئ">
              <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="#fb923c" strokeWidth={2.2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z"/>
              </svg>
            </HdrBtn>

            {/* Profile button */}
            <HdrBtn onClick={() => setShowProfile(true)} title="الملف الشخصي">
              <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
              </svg>
            </HdrBtn>

            <HdrBtn onClick={() => setShowNotif(true)} title="الإشعارات">
              <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"/>
              </svg>
              {unreadCount > 0 && <NotifDot count={unreadCount} />}
            </HdrBtn>

            <HdrBtn onClick={() => setDarkMode(d => !d)} title="الوضع الليلي">
              <span style={{ fontSize:16 }}>{darkMode ? '☀️' : '🌙'}</span>
            </HdrBtn>

            <button onClick={() => setShowLogout(true)} style={{ background:'rgba(239,68,68,0.15)', border:'1px solid rgba(239,68,68,0.3)', borderRadius:10, padding:'7px 14px', color:'#fca5a5', fontSize:12, fontWeight:800, cursor:'pointer', fontFamily:"'Tajawal',sans-serif" }}>
              خروج
            </button>
          </div>
        </div>

        {/* Tabs bar */}
        <div style={{ maxWidth:1400, margin:'0 auto', padding:'0 20px', display:'flex', gap:0, overflowX:'auto' }}>
          {([
            { id:'overview',    label:'لوحة المؤشرات',           icon:'📊' },
            { id:'dept-events', label:'أحداث القسم بالكامل',     icon:'🗂️', badge: filteredDept.length || null },
            { id:'my-events',   label:'بلاغاتي الشخصية',         icon:'👤', badge: myIncidents.length || null },
            { id:'rca-done',    label:'الحالات المُحللة جذرياً', icon:'✅', badge: rcaDone.length || null },
            { id:'new-event',   label:'تسجيل حدث',               icon:'➕' },
          ] as { id:TabId; label:string; icon:string; badge?:number|null }[]).map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
              padding:'10px 20px', border:'none', cursor:'pointer',
              fontFamily:"'Tajawal',sans-serif", fontWeight:800, fontSize:13,
              display:'flex', alignItems:'center', gap:6,
              background:'transparent', whiteSpace:'nowrap',
              color: activeTab === tab.id ? '#fbbf24' : '#94a3b8',
              borderBottom: activeTab === tab.id ? '3px solid #fbbf24' : '3px solid transparent',
              transition:'all 0.2s',
            }}>
              <span>{tab.icon}</span>
              <span>{tab.label}</span>
              {tab.badge ? (
                <span style={{ background: activeTab === tab.id ? '#f59e0b' : 'rgba(255,255,255,0.1)', color:'#fff', borderRadius:20, padding:'1px 7px', fontSize:10, fontWeight:900 }}>
                  {tab.badge}
                </span>
              ) : null}
            </button>
          ))}
        </div>
      </header>

      <main style={{ maxWidth:1400, margin:'0 auto', padding:'24px 20px 60px' }}>

        {/* ══ TAB: OVERVIEW ══ */}
        {activeTab === 'overview' && (
          <div>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
              <div style={{ color: darkMode ? '#fff' : '#1f3b73', fontSize:20, fontWeight:900 }}>📡 الرصد اللحظي للمؤشرات</div>
              <button onClick={() => window.location.reload()} style={btnSecondary(darkMode)}>🔄 تحديث مباشر</button>
            </div>

            {/* KPI cards */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(240px,1fr))', gap:16, marginBottom:24 }}>
              {[
                { label:'الأحداث المفتوحة (بالقسم)', value:kpiOpen,      icon:'📂', color:'#2563eb', border:'#3b82f6' },
                { label:'أحداث تتطلب تدخلك (مصعدة)', value:kpiEscalated, icon:'🔺', color:'#ea580c', border:'#f97316' },
                { label:'تجاوزت الوقت المعياري SLA',  value:kpiBreach,   icon:'⚠️', color:'#dc2626', border:'#ef4444' },
              ].map(kpi => (
                <div key={kpi.label} style={{
                  background: darkMode ? 'rgba(15,23,42,0.9)' : '#fff',
                  borderRadius:20, padding:'24px',
                  border:`1px solid ${darkMode ? '#1e293b' : '#e2e8f0'}`,
                  borderRight:`4px solid ${kpi.border}`,
                  boxShadow:'0 4px 16px rgba(0,0,0,0.05)', position:'relative', overflow:'hidden',
                }}>
                  <div style={{ color:'#94a3b8', fontSize:12, fontWeight:700, marginBottom:8 }}>{kpi.label}</div>
                  <div style={{ color: kpi.color, fontSize:48, fontWeight:900, lineHeight:1 }}>{kpi.value}</div>
                  <span style={{ position:'absolute', left:16, bottom:12, fontSize:48, opacity:0.05 }}>{kpi.icon}</span>
                </div>
              ))}
            </div>

            {/* Charts */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
              <ChartCard title="توزيع الأحداث حسب الحالة" icon="📊" data={statusDist} total={deptIncidents.length} darkMode={darkMode} colorMap={{ 'مفتوح':'#3b82f6','مصعد':'#f97316','موجه':'#0ea5e9','طلب RCA':'#a855f7','مغلق':'#94a3b8','RCA Completed':'#10b981' }} />
              <ChartCard title="توزيع الأحداث حسب الخطورة" icon="🛡️" data={riskDist} total={deptIncidents.length} darkMode={darkMode} colorMap={{ 'منخفض':'#10b981','متوسط':'#f59e0b','عالي':'#ea580c','مرتفع':'#ea580c','حرج':'#ef4444' }} />
            </div>
          </div>
        )}

        {/* ══ TAB: DEPT EVENTS ══ */}
        {activeTab === 'dept-events' && (
          <div>
            <div style={{ display:'flex', flexWrap:'wrap', gap:10, marginBottom:12, alignItems:'center', justifyContent:'space-between' }}>
              <div style={{ color: darkMode ? '#fff' : '#1f3b73', fontSize:18, fontWeight:900 }}>🗂️ البلاغات المرفوعة من القسم</div>
              <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center' }}>
                <SearchInput value={deptSearch} onChange={v => { setDeptSearch(v); setDeptPage(1); }} darkMode={darkMode} />
                <StatusSelect value={deptStatus} onChange={v => { setDeptStatus(v); setDeptPage(1); }} darkMode={darkMode} />
                <span style={{ color:'#94a3b8', fontSize:12, fontWeight:700 }}>{filteredDept.length} نتيجة</span>
                {/* Batch toggle */}
                <button
                  onClick={() => { setBatchMode(m => !m); setSelectedBatch(new Set()); }}
                  style={{ background: batchMode ? 'rgba(37,99,235,0.15)' : (darkMode ? '#1e293b' : '#fff'), border:`1px solid ${batchMode ? '#3b82f6' : (darkMode ? '#334155' : '#e2e8f0')}`, borderRadius:10, padding:'8px 14px', color: batchMode ? '#3b82f6' : '#94a3b8', fontSize:12, fontWeight:800, cursor:'pointer', fontFamily:"'Tajawal',sans-serif", display:'flex', alignItems:'center', gap:6 }}
                >
                  ☑️ {batchMode ? 'إلغاء التحديد الجماعي' : 'تحديد جماعي'}
                </button>
                {/* Print list */}
                <button
                  onClick={() => printList(filteredDept, 'أحداث القسم')}
                  style={{ background: darkMode ? '#1e293b' : '#fff', border:`1px solid ${darkMode ? '#334155' : '#e2e8f0'}`, borderRadius:10, padding:'8px 14px', color:'#64748b', fontSize:12, fontWeight:800, cursor:'pointer', fontFamily:"'Tajawal',sans-serif", display:'flex', alignItems:'center', gap:6 }}
                >
                  🖨️ طباعة القائمة
                </button>
              </div>
            </div>

            {/* Batch action bar */}
            {batchMode && (
              <div style={{ background: darkMode ? '#1e3a5f' : '#eff6ff', border:'1px solid #3b82f6', borderRadius:14, padding:'12px 16px', marginBottom:14, display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, flexWrap:'wrap' }}>
                <span style={{ color:'#2563eb', fontWeight:900, fontSize:13 }}>
                  ☑️ تم تحديد {selectedBatch.size} حدث
                </span>
                <div style={{ display:'flex', gap:8 }}>
                  <button
                    onClick={() => executeBatch('close')}
                    disabled={batchPending || selectedBatch.size === 0}
                    style={{ background:'#059669', border:'none', borderRadius:10, padding:'8px 16px', color:'#fff', fontWeight:900, fontSize:12, cursor: batchPending || selectedBatch.size === 0 ? 'not-allowed' : 'pointer', fontFamily:"'Tajawal',sans-serif", opacity: selectedBatch.size === 0 ? 0.5 : 1 }}
                  >
                    {batchPending ? '⏳...' : '✅ إغلاق المحدد'}
                  </button>
                  <button
                    onClick={() => executeBatch('escalate')}
                    disabled={batchPending || selectedBatch.size === 0}
                    style={{ background:'#ea580c', border:'none', borderRadius:10, padding:'8px 16px', color:'#fff', fontWeight:900, fontSize:12, cursor: batchPending || selectedBatch.size === 0 ? 'not-allowed' : 'pointer', fontFamily:"'Tajawal',sans-serif", opacity: selectedBatch.size === 0 ? 0.5 : 1 }}
                  >
                    {batchPending ? '⏳...' : '🔺 تصعيد المحدد'}
                  </button>
                </div>
              </div>
            )}

            {loadingDept ? <SkeletonGrid /> : filteredDept.length === 0 ? (
              <EmptyBox msg="لا توجد أحداث في القسم" darkMode={darkMode} />
            ) : (
              <>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(320px,1fr))', gap:16, marginBottom:20 }}>
                  {slicedDept.map(inc => (
                    <IncidentCard
                      key={inc.id}
                      incident={inc}
                      darkMode={darkMode}
                      onClick={() => { if (batchMode) toggleBatch(inc.id); else setSelectedId(inc.id); }}
                      batchMode={batchMode}
                      isSelected={selectedBatch.has(inc.id)}
                      onToggle={() => toggleBatch(inc.id)}
                      onRcaView={inc.status === 'RCA Completed' ? () => setRcaViewId(inc.id) : undefined}
                    />
                  ))}
                </div>
                <Pagination page={deptPage} total={Math.max(1,Math.ceil(filteredDept.length/PAGE_SIZE))} onChange={setDeptPage} darkMode={darkMode} />
              </>
            )}
          </div>
        )}

        {/* ══ TAB: MY EVENTS ══ */}
        {activeTab === 'my-events' && (
          <div>
            <div style={{ display:'flex', flexWrap:'wrap', gap:10, marginBottom:20, alignItems:'center', justifyContent:'space-between' }}>
              <div style={{ color: darkMode ? '#fff' : '#1f3b73', fontSize:18, fontWeight:900 }}>👤 سجل بلاغاتي الشخصية</div>
              <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center' }}>
                <SearchInput value={mySearch} onChange={v => { setMySearch(v); setMyPage(1); }} darkMode={darkMode} />
                <StatusSelect value={myStatus} onChange={v => { setMyStatus(v); setMyPage(1); }} darkMode={darkMode} />
                <span style={{ color:'#94a3b8', fontSize:12, fontWeight:700 }}>{filteredMy.length} نتيجة</span>
              </div>
            </div>

            {loadingMy ? <SkeletonGrid /> : filteredMy.length === 0 ? (
              <EmptyBox msg="لا توجد بلاغات شخصية" darkMode={darkMode} />
            ) : (
              <>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(320px,1fr))', gap:16, marginBottom:20 }}>
                  {slicedMy.map(inc => (
                    <IncidentCard key={inc.id} incident={inc} darkMode={darkMode} onClick={() => setSelectedId(inc.id)} />
                  ))}
                </div>
                <Pagination page={myPage} total={Math.max(1,Math.ceil(filteredMy.length/PAGE_SIZE))} onChange={setMyPage} darkMode={darkMode} />
              </>
            )}
          </div>
        )}

        {/* ══ TAB: RCA DONE ══ */}
        {activeTab === 'rca-done' && (
          <div>
            <div style={{ display:'flex', flexWrap:'wrap', gap:10, marginBottom:16, alignItems:'center', justifyContent:'space-between' }}>
              <div style={{ color:'#16a34a', fontSize:18, fontWeight:900 }}>✅ الحالات المُحللة جذرياً (RCA Completed)</div>
              <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                <SearchInput value={rcaSearch} onChange={setRcaSearch} darkMode={darkMode} placeholder="بحث في الحالات..." />
                <span style={{ color:'#94a3b8', fontSize:12, fontWeight:700 }}>{rcaDone.length} نتيجة</span>
              </div>
            </div>
            <div style={{ background:'#f0fdf4', border:'1px solid #bbf7d0', borderRadius:14, padding:'12px 16px', marginBottom:16, display:'flex', alignItems:'center', gap:8, color:'#166534', fontSize:12, fontWeight:700 }}>
              ℹ️ هذه الحالات أُجري عليها تحليل سبب جذري كامل. يُرجى الرجوع إليها لتفادي تكرار الأحداث.
            </div>
            {rcaDone.length === 0 ? (
              <EmptyBox msg="لا توجد حالات محللة جذرياً بعد" darkMode={darkMode} />
            ) : (
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(320px,1fr))', gap:16 }}>
                {rcaDone.map(inc => (
                  <IncidentCard key={inc.id} incident={inc} darkMode={darkMode} onClick={() => setSelectedId(inc.id)} rcaDone />
                ))}
              </div>
            )}
          </div>
        )}

        {/* ══ TAB: NEW INCIDENT ══ */}
        {activeTab === 'new-event' && (
          <NewIncidentForm
            departments={departments}
            categories={categories}
            profile={profile}
            onSuccess={() => { showToast('تم تسجيل الحدث بنجاح ✅', 'success'); setActiveTab('dept-events'); }}
            onError={(msg) => showToast(msg, 'error')}
            darkMode={darkMode}
          />
        )}
      </main>

      {/* ══ DETAILS MODAL ══ */}
      {selectedId && (
        <ActionModal
          id={selectedId}
          profile={profile}
          isDutyMgrPlus={isDutyMgrPlus}
          departments={departments}
          deptEmployees={deptEmployees}
          darkMode={darkMode}
          onClose={() => setSelectedId(null)}
          onSuccess={(msg) => { showToast(msg, 'success'); setSelectedId(null); }}
          onError={(msg) => showToast(msg, 'error')}
        />
      )}

      {/* ══ NOTIFICATIONS MODAL ══ */}
      {showNotif && (
        <NotifModal
          notifications={notifications}
          unreadCount={unreadCount}
          darkMode={darkMode}
          onClose={() => setShowNotif(false)}
          onMarkAll={handleMarkAllRead}
          onClickNotif={(nid, incId) => { markRead([nid]); setShowNotif(false); if (incId) setSelectedId(incId); }}
        />
      )}

      {/* ══ PROFILE MODAL ══ */}
      {showProfile && (
        <ProfileModal
          profile={profile}
          darkMode={darkMode}
          onClose={() => setShowProfile(false)}
          onSuccess={(msg) => { showToast(msg, 'success'); setShowProfile(false); }}
          onError={(msg) => showToast(msg, 'error')}
        />
      )}

      {/* ══ CALL MODAL ══ */}
      {showCall && (
        <CallModal
          profile={profile}
          departments={departments}
          darkMode={darkMode}
          onClose={() => setShowCall(false)}
          onSuccess={(msg) => { showToast(msg, 'success'); }}
          onError={(msg) => showToast(msg, 'error')}
        />
      )}

      {/* ══ RCA VIEW MODAL ══ */}
      {rcaViewId && (
        <RcaViewModal
          incidentId={rcaViewId}
          darkMode={darkMode}
          onClose={() => setRcaViewId(null)}
        />
      )}

      {/* ══ LOGOUT MODAL ══ */}
      {showLogout && (
        <LogoutModal
          darkMode={darkMode}
          onConfirm={handleSignOut}
          onCancel={() => setShowLogout(false)}
        />
      )}

      <style>{`@keyframes fadeInUp{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}} @keyframes modalIn{from{opacity:0;transform:scale(0.95)}to{opacity:1;transform:scale(1)}} @keyframes shimmer{0%{background-position:200% 0}100%{background-position:-200% 0}} @keyframes emergencyPulse{0%,100%{box-shadow:0 0 0 0 rgba(239,68,68,0.4)}50%{box-shadow:0 0 0 10px rgba(239,68,68,0)}}`}</style>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   ACTION / DETAILS MODAL
══════════════════════════════════════════════════════════════ */
function ActionModal({ id, profile, isDutyMgrPlus, departments, deptEmployees, darkMode, onClose, onSuccess, onError }: {
  id: string; profile: any; isDutyMgrPlus: boolean;
  departments: Department[]; deptEmployees: DeptEmployee[];
  darkMode: boolean; onClose: () => void;
  onSuccess: (msg: string) => void; onError: (msg: string) => void;
}) {
  const { data: incident, isLoading } = useIncident(id);
  const { mutateAsync: doAction, isPending } = useIncidentAction();

  const [actionType, setActionType]   = useState('update');
  const [actionDesc, setActionDesc]   = useState('');
  const [targetDept, setTargetDept]   = useState('');
  const [targetEmp,  setTargetEmp]    = useState('');
  const [escalateTo, setEscalateTo]   = useState<'medical_director'|'executive_director'>('medical_director');

  const bg    = darkMode ? '#0f172a' : '#fff';
  const bgSub = darkMode ? '#1e293b' : '#f8fafc';
  const isClosed = incident && ['مغلق','RCA Completed'].includes(incident.status);

  const handleSubmit = async () => {
    if (!actionDesc.trim() && actionType !== 'rca') { onError('يرجى كتابة نص الإجراء'); return; }
    const target = actionType === 'direct' ? targetDept : actionType === 'assign' ? targetEmp : undefined;
    try {
      await doAction({
        incident_id: id,
        action_type: actionType as any,
        description: actionDesc || undefined,
        target,
        escalate_to: actionType === 'escalate' ? escalateTo : undefined,
      });
      onSuccess(actionType === 'close' ? 'تم إغلاق الحدث ✅' : 'تم تنفيذ القرار ✅');
    } catch (e: any) { onError(e?.message ?? 'حدث خطأ'); }
  };

  const handlePrint = () => window.print();

  return (
    <div onClick={onClose} style={{ position:'fixed', inset:0, zIndex:60, background:'rgba(15,23,42,0.8)', backdropFilter:'blur(8px)', display:'flex', justifyContent:'center', alignItems:'center', padding:16 }}>
      <div onClick={e => e.stopPropagation()} style={{
        background:bg, width:'100%', maxWidth:800, borderRadius:28,
        overflow:'hidden', display:'flex', flexDirection:'column',
        maxHeight:'92vh', animation:'modalIn 0.25s ease',
        border:`1px solid ${darkMode ? '#334155' : '#e2e8f0'}`,
        boxShadow:'0 32px 80px rgba(0,0,0,0.35)',
      }}>
        {/* Modal header */}
        <div style={{ background:'linear-gradient(135deg,#0f172a,#1e293b)', padding:'18px 22px', display:'flex', justifyContent:'space-between', alignItems:'center', position:'relative', overflow:'hidden' }}>
          <div style={{ position:'absolute', right:-20, top:-20, width:120, height:120, background:'rgba(59,130,246,0.15)', borderRadius:'50%' }} />
          <div style={{ color:'#fff', fontWeight:900, fontSize:16, display:'flex', alignItems:'center', gap:10, position:'relative' }}>
            <span style={{ fontSize:22 }}>🛡️</span>
            مركز إدارة البلاغ
            {incident && <span style={{ background:'rgba(255,255,255,0.1)', border:'1px solid rgba(255,255,255,0.2)', color:'#94a3b8', fontFamily:'monospace', fontSize:11, padding:'3px 10px', borderRadius:8 }}>{incident.event_id}</span>}
          </div>
          <div style={{ display:'flex', gap:8, position:'relative' }}>
            <button onClick={handlePrint} style={{ background:'rgba(255,255,255,0.1)', border:'1px solid rgba(255,255,255,0.2)', borderRadius:10, padding:'6px 12px', color:'rgba(255,255,255,0.8)', fontSize:11, fontWeight:800, cursor:'pointer', display:'flex', alignItems:'center', gap:5, fontFamily:"'Tajawal',sans-serif" }}>
              🖨️ طباعة PDF
            </button>
            <button onClick={onClose} style={{ background:'none', border:'none', color:'rgba(255,255,255,0.6)', fontSize:22, cursor:'pointer' }}>✕</button>
          </div>
        </div>

        {isLoading ? (
          <div style={{ padding:60, textAlign:'center', color:'#94a3b8', fontWeight:700 }}>⏳ جارٍ التحميل...</div>
        ) : incident ? (
          <div style={{ overflowY:'auto', flex:1, padding:'20px' }}>

            {/* Info grid */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:10, marginBottom:16 }}>
              {[
                { label:'الموظف المُبلغ', value: incident.reporter_name, accent:'#3b82f6' },
                { label:'القسم والموقع',  value: incident.location,      accent:'#6366f1' },
                { label:'مستوى الخطورة', value: incident.risk_level,    accent:'#ef4444' },
              ].map(d => (
                <div key={d.label} style={{ background:bgSub, borderRadius:14, padding:'12px 14px', border:`1px solid ${darkMode ? '#334155' : '#e2e8f0'}`, position:'relative', overflow:'hidden' }}>
                  <div style={{ position:'absolute', right:0, top:0, bottom:0, width:3, background:d.accent, opacity:0.5 }} />
                  <div style={{ color:'#94a3b8', fontSize:10, fontWeight:700, marginBottom:4 }}>{d.label}</div>
                  <div style={{ fontWeight:900, fontSize:13 }}>{d.value}</div>
                </div>
              ))}
              <div style={{ background:bgSub, borderRadius:14, padding:'12px 14px', border:`1px solid ${darkMode ? '#334155' : '#e2e8f0'}`, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center' }}>
                <div style={{ color:'#94a3b8', fontSize:10, fontWeight:700, marginBottom:6 }}>حالة البلاغ</div>
                {(() => { const s = STATUS_STYLE[incident.status] ?? STATUS_STYLE['مفتوح']; return (
                  <span style={{ background:s.bg, color:s.color, border:`1px solid ${s.border}`, borderRadius:10, padding:'4px 14px', fontSize:12, fontWeight:900 }}>{incident.status}</span>
                ); })()}
              </div>
            </div>

            {/* Description */}
            <div style={{ background:bgSub, borderRadius:14, padding:16, marginBottom:14, border:`1px solid ${darkMode ? '#334155' : '#e2e8f0'}` }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
                <div style={{ color:'#94a3b8', fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:1 }}>الوصف الميداني الكامل</div>
                <button
                  onClick={() => shareIncidentWhatsApp(incident)}
                  title="مشاركة عبر واتساب"
                  style={{ background:'none', border:'none', cursor:'pointer', padding:'2px 6px', borderRadius:8, display:'flex', alignItems:'center', gap:4, color:'#25d366', fontSize:11, fontWeight:800, fontFamily:"'Tajawal',sans-serif" }}
                >
                  {waIcon} مشاركة
                </button>
              </div>
              <p style={{ fontWeight:700, fontSize:14, lineHeight:1.8, margin:0 }}>{incident.description}</p>
            </div>

            {/* Logs */}
            {(incident as any).logs?.length > 0 && (
              <div style={{ marginBottom:14 }}>
                <div style={{ fontWeight:900, fontSize:13, marginBottom:10, display:'flex', alignItems:'center', gap:6 }}>
                  📜 التسلسل الزمني للإجراءات
                </div>
                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  {(incident as any).logs.map((log: any) => {
                    const s = LOG_STYLE[log.action_type] ?? LOG_STYLE['update'];
                    return (
                      <div key={log.id} style={{ background:s.bg, borderRadius:12, padding:'10px 14px', border:'1px solid rgba(0,0,0,0.05)' }}>
                        <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}>
                          <span style={{ color:s.color, fontSize:12, fontWeight:800 }}>{s.icon} {log.action_name ?? log.action_type}</span>
                          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                            {log.description && (
                              <button
                                onClick={() => shareUpdateWhatsApp(incident.event_id, log.description, log.actor_name ?? 'النظام')}
                                title="مشاركة عبر واتساب"
                                style={{ background:'none', border:'none', cursor:'pointer', padding:0, display:'flex', alignItems:'center' }}
                              >
                                {waIcon}
                              </button>
                            )}
                            <span style={{ color:'#94a3b8', fontSize:10 }} dir="ltr">{new Date(log.created_at).toLocaleString('en-GB', { dateStyle:'short', timeStyle:'short' })}</span>
                          </div>
                        </div>
                        {log.description && <p style={{ color:'#374151', fontSize:12, fontWeight:600, margin:0, lineHeight:1.5 }}>{log.description}</p>}
                        <div style={{ color:'#94a3b8', fontSize:10, fontWeight:700, marginTop:4 }}>بواسطة: {log.actor_name ?? 'النظام'}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Action Panel */}
            {!isClosed ? (
              <div style={{ background: darkMode ? 'linear-gradient(135deg,#1e293b,#1e3a5f)' : 'linear-gradient(135deg,#eff6ff,#eef2ff)', borderRadius:20, padding:20, border:`1px solid ${darkMode ? '#1e3a5f' : '#bfdbfe'}` }}>
                <div style={{ color: darkMode ? '#93c5fd' : '#1d4ed8', fontWeight:900, fontSize:14, marginBottom:16, display:'flex', alignItems:'center', gap:6 }}>
                  ⚖️ اتخاذ قرار توجيهي / إداري
                </div>

                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:14 }}>
                  {/* Action type */}
                  <div>
                    <label style={{ display:'block', color:'#94a3b8', fontSize:11, fontWeight:800, marginBottom:6 }}>نوع الإجراء المطلوب</label>
                    <select value={actionType} onChange={e => setActionType(e.target.value)}
                      style={{ width:'100%', ...selectStyle(darkMode) }}>
                      <option value="update">تحديث ومتابعة</option>
                      <option value="assign">إسناد لموظف داخلي بالقسم</option>
                      <option value="direct">توجيه واعتماد لقسم آخر</option>
                      <option value="rca">طلب تحليل جذري RCA</option>
                      {isDutyMgrPlus && <option value="escalate">تصعيد للإدارة العليا</option>}
                      <option value="close">إغلاق الحدث نهائياً</option>
                    </select>
                  </div>

                  {/* Direct to dept */}
                  {actionType === 'direct' && (
                    <div>
                      <label style={{ display:'block', color:'#7c3aed', fontSize:11, fontWeight:800, marginBottom:6 }}>القسم المعني بالتوجيه</label>
                      <select value={targetDept} onChange={e => setTargetDept(e.target.value)}
                        style={{ width:'100%', ...selectStyle(darkMode), borderColor:'#c4b5fd', background: darkMode ? '#1e1b4b' : '#f5f3ff' }}>
                        <option value="">اختر القسم...</option>
                        {departments.map(d => <option key={d.id} value={d.name}>{d.name}</option>)}
                      </select>
                    </div>
                  )}

                  {/* Assign to employee */}
                  {actionType === 'assign' && (
                    <div>
                      <label style={{ display:'block', color:'#059669', fontSize:11, fontWeight:800, marginBottom:6 }}>الموظف المسند إليه</label>
                      <select value={targetEmp} onChange={e => setTargetEmp(e.target.value)}
                        style={{ width:'100%', ...selectStyle(darkMode), borderColor:'#6ee7b7', background: darkMode ? '#064e3b' : '#f0fdf4' }}>
                        <option value="">اختر الموظف...</option>
                        {deptEmployees.map(e => <option key={e.id} value={e.name}>{e.name}</option>)}
                      </select>
                    </div>
                  )}
                </div>

                {/* Escalation target */}
                {actionType === 'escalate' && isDutyMgrPlus && (
                  <div style={{ marginBottom:14 }}>
                    <label style={{ display:'block', color:'#dc2626', fontSize:11, fontWeight:800, marginBottom:8 }}>جهة التصعيد</label>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                      {[
                        { value:'medical_director',   label:'المدير الطبي',    sub:'تصعيد طبي وسريري',   bg:'#fef2f2', border:'#fca5a5', color:'#dc2626' },
                        { value:'executive_director', label:'المدير التنفيذي', sub:'تصعيد إداري وتنفيذي', bg:'#faf5ff', border:'#d8b4fe', color:'#9333ea' },
                      ].map(opt => (
                        <label key={opt.value} onClick={() => setEscalateTo(opt.value as any)} style={{
                          display:'flex', alignItems:'center', gap:10, padding:'12px 14px',
                          borderRadius:12, border:`2px solid ${escalateTo === opt.value ? opt.color : opt.border}`,
                          background: escalateTo === opt.value ? opt.bg : 'transparent',
                          cursor:'pointer', transition:'all 0.2s',
                        }}>
                          <input type="radio" checked={escalateTo === opt.value} onChange={() => setEscalateTo(opt.value as any)} style={{ accentColor: opt.color }} />
                          <div>
                            <div style={{ color: opt.color, fontSize:13, fontWeight:900 }}>{opt.label}</div>
                            <div style={{ color:'#94a3b8', fontSize:11, fontWeight:600 }}>{opt.sub}</div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {/* Description */}
                <div style={{ marginBottom:14 }}>
                  <label style={{ display:'block', color:'#94a3b8', fontSize:11, fontWeight:800, marginBottom:6 }}>نص التوجيه أو الإجراء المتخذ</label>
                  <textarea rows={3} value={actionDesc} onChange={e => setActionDesc(e.target.value)}
                    placeholder="اكتب قرارك بوضوح لتوجيه الأقسام المعنية..."
                    style={{ width:'100%', borderRadius:12, border:`1px solid ${darkMode ? '#334155' : '#bfdbfe'}`, padding:'10px 14px', fontSize:13, fontWeight:600, fontFamily:"'Tajawal',sans-serif", resize:'none', outline:'none', background: darkMode ? '#0f172a' : '#fff', color: darkMode ? '#f1f5f9' : '#1e293b', boxSizing:'border-box' }} />
                </div>

                <button onClick={handleSubmit} disabled={isPending} style={{
                  width:'100%', background: isPending ? '#94a3b8' : 'linear-gradient(135deg,#0f172a,#1e3a5f)',
                  border:'none', borderRadius:12, padding:'15px 0', color:'#fff',
                  fontSize:15, fontWeight:900, fontFamily:"'Tajawal',sans-serif",
                  cursor: isPending ? 'not-allowed' : 'pointer',
                  display:'flex', alignItems:'center', justifyContent:'center', gap:8,
                  boxShadow: isPending ? 'none' : '0 8px 20px rgba(15,23,42,0.3)',
                }}>
                  {isPending ? '⏳ جارٍ التنفيذ...' : '✅ تنفيذ واعتماد القرار'}
                </button>
              </div>
            ) : (
              <div style={{ background:'#f8fafc', borderRadius:16, padding:20, border:'1px solid #e2e8f0', textAlign:'center' }}>
                <div style={{ fontSize:36, marginBottom:8 }}>✅</div>
                <div style={{ color:'#64748b', fontWeight:800 }}>هذا الحدث مغلق نهائياً.</div>
              </div>
            )}
          </div>
        ) : (
          <div style={{ padding:40, textAlign:'center', color:'#ef4444', fontWeight:800 }}>تعذّر تحميل البيانات</div>
        )}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   CHART CARD (بديل Chart.js — CSS bars)
══════════════════════════════════════════════════════════════ */
function ChartCard({ title, icon, data, total, darkMode, colorMap }: {
  title: string; icon: string; data: [string, number][];
  total: number; darkMode: boolean; colorMap: Record<string, string>;
}) {
  const bg = darkMode ? 'rgba(15,23,42,0.9)' : '#fff';
  return (
    <div style={{ background:bg, borderRadius:20, padding:20, border:`1px solid ${darkMode ? '#1e293b' : '#e2e8f0'}`, boxShadow:'0 4px 16px rgba(0,0,0,0.04)' }}>
      <div style={{ color:'#94a3b8', fontSize:11, fontWeight:900, textTransform:'uppercase', letterSpacing:1, marginBottom:16 }}>
        {icon} {title}
      </div>
      {total === 0 ? (
        <div style={{ textAlign:'center', color:'#94a3b8', fontSize:12, fontWeight:700, padding:'20px 0' }}>لا توجد بيانات كافية</div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
          {data.map(([label, count]) => {
            const pct = Math.round((count / total) * 100);
            const color = colorMap[label] ?? '#94a3b8';
            return (
              <div key={label}>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}>
                  <span style={{ fontSize:12, fontWeight:800 }}>{label}</span>
                  <span style={{ fontSize:12, fontWeight:900, color }}>{count} ({pct}%)</span>
                </div>
                <div style={{ background: darkMode ? '#1e293b' : '#f1f5f9', borderRadius:99, height:8, overflow:'hidden' }}>
                  <div style={{ width:`${pct}%`, height:'100%', background:color, borderRadius:99, transition:'width 0.5s' }} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   INCIDENT CARD
══════════════════════════════════════════════════════════════ */
function IncidentCard({ incident, darkMode, onClick, rcaDone, batchMode, isSelected, onToggle, onRcaView }: {
  incident: IncidentRow; darkMode: boolean; onClick: () => void; rcaDone?: boolean;
  batchMode?: boolean; isSelected?: boolean; onToggle?: () => void; onRcaView?: () => void;
}) {
  const statusStyle = STATUS_STYLE[incident.status] ?? STATUS_STYLE['مفتوح'];
  const borderColor = isSelected ? '#3b82f6' : rcaDone ? '#10b981' : (RISK_BORDER[incident.risk_level] ?? '#3b82f6');
  const sla = useSlaStatus(incident.created_at, incident.sla_minutes, incident.closed_at ?? null, incident.status);

  return (
    <div style={{ position:'relative' }}>
      {/* Batch checkbox overlay */}
      {batchMode && (
        <div
          onClick={e => { e.stopPropagation(); onToggle?.(); }}
          style={{ position:'absolute', top:10, left:10, zIndex:10, width:22, height:22, borderRadius:6, border:`2px solid ${isSelected ? '#3b82f6' : '#94a3b8'}`, background: isSelected ? '#3b82f6' : (darkMode ? '#1e293b' : '#fff'), display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', fontSize:13, color:'#fff' }}
        >
          {isSelected ? '✓' : ''}
        </div>
      )}

      <div onClick={onClick} style={{
        background: isSelected ? (darkMode ? 'rgba(37,99,235,0.15)' : '#eff6ff') : (darkMode ? 'rgba(15,23,42,0.85)' : '#fff'),
        borderRadius:20, padding:'18px 20px',
        boxShadow:'0 4px 16px rgba(0,0,0,0.05)',
        border:`1px solid ${isSelected ? '#3b82f6' : (darkMode ? '#1e293b' : '#e2e8f0')}`,
        borderRight:`4px solid ${borderColor}`,
        cursor:'pointer', transition:'all 0.3s cubic-bezier(0.175,0.885,0.32,1.275)',
      }}
      onMouseEnter={e => { (e.currentTarget as HTMLElement).style.transform='translateY(-4px) scale(1.01)'; (e.currentTarget as HTMLElement).style.boxShadow='0 20px 40px rgba(0,0,0,0.1)'; }}
      onMouseLeave={e => { (e.currentTarget as HTMLElement).style.transform=''; (e.currentTarget as HTMLElement).style.boxShadow='0 4px 16px rgba(0,0,0,0.05)'; }}>

        {/* Top row */}
        <div style={{ display:'flex', justifyContent:'space-between', marginBottom:8 }}>
          <span style={{ background: darkMode ? '#1e293b' : '#f1f5f9', color:'#64748b', fontFamily:'monospace', fontSize:11, fontWeight:800, padding:'2px 8px', borderRadius:6 }}>{incident.event_id}</span>
          <span style={{ background:statusStyle.bg, color:statusStyle.color, border:`1px solid ${statusStyle.border}`, borderRadius:20, padding:'3px 10px', fontSize:10, fontWeight:800 }}>{incident.status}</span>
        </div>

        {/* Reporter */}
        <div style={{ color:'#94a3b8', fontSize:11, fontWeight:600, marginBottom:4 }}>👤 {incident.reporter_name}</div>

        {/* Description */}
        <p style={{ color: darkMode ? '#e2e8f0' : '#1e293b', fontSize:13, fontWeight:800, lineHeight:1.5, marginBottom:8, display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical', overflow:'hidden' }}>
          {incident.description}
        </p>

        {/* Location + risk */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
          <span style={{ color:'#94a3b8', fontSize:11, fontWeight:600 }}>📍 {incident.location}</span>
          <span style={{ background: RISK_GRADIENT[incident.risk_level] ?? RISK_GRADIENT['منخفض'], color:'#fff', borderRadius:20, padding:'2px 10px', fontSize:10, fontWeight:900 }}>
            {incident.risk_level}
          </span>
        </div>

        {/* SLA */}
        <SlaBar sla={sla} status={incident.status} darkMode={darkMode} />

        {/* Date */}
        <div style={{ color:'#94a3b8', fontSize:10, fontWeight:600, marginTop:8, textAlign:'left' }} dir="ltr">
          {new Date(incident.created_at).toLocaleString('en-GB', { dateStyle:'short', timeStyle:'short' })}
        </div>

        {/* RCA View button */}
        {onRcaView && (
          <button
            onClick={e => { e.stopPropagation(); onRcaView(); }}
            style={{ marginTop:10, width:'100%', background:'linear-gradient(135deg,#4c1d95,#6d28d9)', border:'none', borderRadius:10, padding:'8px 0', color:'#fff', fontSize:12, fontWeight:900, cursor:'pointer', fontFamily:"'Tajawal',sans-serif", display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}
          >
            📋 عرض تقرير RCA
          </button>
        )}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   SLA BAR
══════════════════════════════════════════════════════════════ */
function SlaBar({ sla, status, darkMode }: { sla: ReturnType<typeof useSlaStatus>; status: string; darkMode: boolean }) {
  if (status === 'مغلق' || status === 'RCA Completed') {
    return <div style={{ background: darkMode ? '#1e293b' : '#f1f5f9', borderRadius:8, padding:'6px 10px', textAlign:'center' }}>
      <span style={{ color:'#64748b', fontSize:10, fontWeight:800 }}>✅ تم الإغلاق نهائياً</span>
    </div>;
  }
  const barColor = sla.isBreached ? '#ef4444' : sla.pct > 80 ? '#f59e0b' : '#10b981';
  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}>
        <span style={{ fontSize:10, fontWeight:800, color: sla.isBreached ? '#ef4444' : '#64748b' }}>
          {sla.isBreached ? '⚠️ تجاوز SLA' : `⏱ متبقي: ${sla.formatRemaining()}`}
        </span>
        <span style={{ fontSize:9, color:'#94a3b8', fontWeight:700 }}>{sla.pct}% استهلاك</span>
      </div>
      <div style={{ background: darkMode ? '#1e293b' : '#e2e8f0', borderRadius:99, height:5, overflow:'hidden' }}>
        <div style={{ width:`${sla.pct}%`, height:'100%', background:barColor, borderRadius:99, transition:'width 0.5s' }} />
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   NEW INCIDENT FORM
══════════════════════════════════════════════════════════════ */
function NewIncidentForm({ departments, categories, profile, onSuccess, onError, darkMode }: {
  departments: Department[]; categories: MainCategory[]; profile: any;
  onSuccess: () => void; onError: (msg: string) => void; darkMode: boolean;
}) {
  const { mutateAsync: createIncident } = useCreateIncident();
  const { register, handleSubmit, reset, watch, setValue, formState: { errors, isSubmitting } } =
    useForm<CreateIncidentInput>({ resolver: zodResolver(createIncidentSchema), defaultValues: { likelihood:1, impact:1, is_repeated:false } });

  const likelihood = watch('likelihood', 1);
  const impact     = watch('impact', 1);
  const riskScore  = likelihood * impact;
  const riskLevel  = computeRiskLevel(riskScore);
  const selectedMain = watch('event_type', '');
  const mainCatForSubs = categories.find(c => c.name === selectedMain);

  const onSubmit = async (data: CreateIncidentInput) => {
    try {
      await createIncident({ ...data, reporter_name: profile?.name ?? '' });
      reset(); onSuccess();
    } catch (e: any) { onError(e?.message ?? 'حدث خطأ'); }
  };

  return (
    <div style={{ background: darkMode ? 'rgba(15,23,42,0.9)' : '#fff', borderRadius:24, padding:'32px', boxShadow:'0 10px 30px rgba(0,0,0,0.06)', border:`1px solid ${darkMode ? '#1e293b' : '#e2e8f0'}`, maxWidth:860, margin:'0 auto' }}>
      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:26, paddingBottom:18, borderBottom:`2px solid ${darkMode ? '#1e293b' : '#f1f5f9'}` }}>
        <div style={{ width:46, height:46, background:'linear-gradient(135deg,#3b82f6,#2563eb)', borderRadius:13, display:'flex', alignItems:'center', justifyContent:'center', fontSize:22 }}>⚡</div>
        <div>
          <div style={{ color:'#1f3b73', fontSize:18, fontWeight:900 }}>تسجيل حدث تشغيلي جديد</div>
          <div style={{ color:'#64748b', fontSize:12, fontWeight:600 }}>أدخل تفاصيل الحدث بدقة</div>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} noValidate style={{ display:'flex', flexDirection:'column', gap:18 }}>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:14 }}>
          <div>
            <FieldLabel label="مكان الحدث" required />
            <select className="input-field" {...register('location')}>
              <option value="">اختر القسم...</option>
              {departments.map(d => <option key={d.id} value={d.name}>{d.name}</option>)}
            </select>
            {errors.location && <ErrMsg msg={errors.location.message} />}
          </div>
          <div>
            <FieldLabel label="التصنيف الرئيسي" required />
            <select className="input-field" {...register('event_type')} onChange={e => setValue('event_type', e.target.value)}>
              <option value="">اختر التصنيف...</option>
              {categories.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
            </select>
            {errors.event_type && <ErrMsg msg={errors.event_type.message} />}
          </div>
          <div>
            <FieldLabel label="التصنيف الفرعي" />
            <select className="input-field" {...register('category_id' as any)}>
              <option value="">بدون تصنيف فرعي</option>
              {mainCatForSubs?.subs.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>

        <div style={{ background: darkMode ? '#1e293b' : '#f8fafc', borderRadius:14, padding:16, border:`1px solid ${darkMode ? '#334155' : '#e2e8f0'}` }}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:14 }}>
            <div>
              <FieldLabel label="احتمالية التكرار" required />
              <select className="input-field" {...register('likelihood', { valueAsNumber:true })}>
                <option value={1}>نادر جدًا (1)</option><option value={2}>نادر (2)</option>
                <option value={3}>محتمل (3)</option><option value={4}>مرجّح (4)</option>
                <option value={5}>شبه مؤكد (5)</option>
              </select>
            </div>
            <div>
              <FieldLabel label="الأثر المتوقع" required />
              <select className="input-field" {...register('impact', { valueAsNumber:true })}>
                <option value={1}>طفيف (1)</option><option value={2}>محدود (2)</option>
                <option value={3}>متوسط (3)</option><option value={4}>جسيم (4)</option>
                <option value={5}>كارثي (5)</option>
              </select>
            </div>
          </div>
          <div style={{ background: darkMode ? '#0f172a' : '#fff', borderRadius:12, padding:'14px 18px', border:`1px solid ${darkMode ? '#334155' : '#e2e8f0'}`, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <span style={{ fontWeight:800, fontSize:14 }}>مستوى الخطورة (Risk Assessment):</span>
            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
              <span style={{ fontWeight:900, fontSize:28, color:'#1f3b73' }}>{riskScore}</span>
              <span style={{ background: RISK_GRADIENT[riskLevel] ?? RISK_GRADIENT['منخفض'], color:'#fff', borderRadius:8, padding:'4px 16px', fontSize:13, fontWeight:900 }}>{riskLevel}</span>
            </div>
          </div>
        </div>

        <div>
          <FieldLabel label="الوصف الميداني بدقة" required />
          <textarea rows={4} placeholder="اكتب تفاصيل ما حدث..." className="input-field" style={{ resize:'none' }} {...register('description')} />
          {errors.description && <ErrMsg msg={errors.description.message} />}
        </div>

        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
          <label style={{ display:'flex', alignItems:'center', gap:10, background: darkMode ? '#1e293b' : '#f8fafc', border:`1px solid ${darkMode ? '#334155' : '#e2e8f0'}`, borderRadius:12, padding:'14px', cursor:'pointer' }}>
            <input type="checkbox" style={{ width:18, height:18, accentColor:'#2563eb' }} {...register('is_repeated')} />
            <span style={{ fontWeight:800, fontSize:13 }}>هل الحدث متكرر؟</span>
          </label>
          <div>
            <FieldLabel label="المرجع / ملف المريض" />
            <input type="text" placeholder="REF-2024-001" className="input-field" dir="ltr" {...register('reference')} />
          </div>
        </div>

        <button type="submit" disabled={isSubmitting} style={{
          background: isSubmitting ? '#94a3b8' : 'linear-gradient(135deg,#1f3b73,#2563eb)',
          border:'none', borderRadius:14, padding:'16px 0', color:'#fff',
          fontSize:16, fontWeight:900, fontFamily:"'Tajawal',sans-serif",
          cursor: isSubmitting ? 'not-allowed' : 'pointer',
          display:'flex', alignItems:'center', justifyContent:'center', gap:8,
          boxShadow: isSubmitting ? 'none' : '0 10px 24px rgba(37,99,235,0.35)',
        }}>
          {isSubmitting ? '⏳ جارٍ التسجيل...' : '📡 اعتماد تسجيل الحدث'}
        </button>
      </form>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   NOTIFICATIONS MODAL
══════════════════════════════════════════════════════════════ */
function NotifModal({ notifications, unreadCount, darkMode, onClose, onMarkAll, onClickNotif }: {
  notifications: any[]; unreadCount: number; darkMode: boolean;
  onClose: () => void; onMarkAll: () => void;
  onClickNotif: (nid: string, incId: string | null) => void;
}) {
  const bg = darkMode ? '#0f172a' : '#fff';
  const NOTIF_ICON: Record<string, string> = { new_event:'⚡', updated:'🔄', escalated:'🔺', escalation:'🔺', rca_request:'🔬', rca_complete:'✅', system:'ℹ️' };
  return (
    <div onClick={onClose} style={{ position:'fixed', inset:0, zIndex:70, background:'rgba(15,23,42,0.6)', backdropFilter:'blur(4px)', display:'flex', justifyContent:'center', alignItems:'center', padding:16 }}>
      <div onClick={e => e.stopPropagation()} style={{ background:bg, width:'100%', maxWidth:460, borderRadius:24, overflow:'hidden', display:'flex', flexDirection:'column', maxHeight:'80vh', animation:'modalIn 0.2s ease', border:`1px solid ${darkMode ? '#334155' : '#e2e8f0'}`, boxShadow:'0 24px 64px rgba(0,0,0,0.25)' }}>
        <div style={{ background:'linear-gradient(135deg,#0f172a,#1e293b)', padding:'14px 18px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div style={{ color:'#fff', fontWeight:900, fontSize:15, display:'flex', alignItems:'center', gap:8 }}>
            🔔 مركز الإشعارات الإشرافية
            {unreadCount > 0 && <span style={{ background:'#ef4444', borderRadius:20, padding:'1px 8px', fontSize:11, fontWeight:900 }}>{unreadCount}</span>}
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'rgba(255,255,255,0.7)', fontSize:18, cursor:'pointer' }}>✕</button>
        </div>
        <div style={{ padding:'8px 16px', borderBottom:`1px solid ${darkMode ? '#1e293b' : '#f1f5f9'}`, display:'flex', justifyContent:'space-between', alignItems:'center', background: darkMode ? '#1e293b' : '#f8fafc' }}>
          <span style={{ color:'#94a3b8', fontSize:11, fontWeight:700 }}>آخر 50 إشعار تشغيلي</span>
          {unreadCount > 0 && <button onClick={onMarkAll} style={{ background:'none', border:'none', color:'#3b82f6', fontSize:11, fontWeight:800, cursor:'pointer', fontFamily:"'Tajawal',sans-serif" }}>تحديد الكل كمقروء ✓</button>}
        </div>
        <div style={{ overflowY:'auto', flex:1 }}>
          {notifications.length === 0 ? (
            <div style={{ padding:40, textAlign:'center' }}><div style={{ fontSize:36 }}>🔕</div><div style={{ color:'#94a3b8', fontWeight:700, marginTop:8 }}>لا توجد إشعارات</div></div>
          ) : notifications.map((n, idx) => (
            <div key={n.id} onClick={() => onClickNotif(n.id, n.incident_id ?? null)} style={{
              display:'flex', alignItems:'flex-start', gap:12, padding:'14px 16px',
              borderBottom: idx < notifications.length - 1 ? `1px solid ${darkMode ? '#1e293b' : '#f8fafc'}` : 'none',
              background: n.is_read ? (darkMode ? '#0f172a' : '#fff') : (darkMode ? '#1e3a5f' : '#eff6ff'),
              cursor:'pointer',
            }}>
              <div style={{ width:36, height:36, background: n.is_read ? (darkMode ? '#1e293b' : '#f1f5f9') : '#dbeafe', borderRadius:10, display:'flex', alignItems:'center', justifyContent:'center', fontSize:18, flexShrink:0 }}>
                {NOTIF_ICON[n.type] ?? 'ℹ️'}
              </div>
              <div style={{ flex:1 }}>
                <p style={{ color: darkMode ? '#e2e8f0' : '#1e293b', fontSize:12, fontWeight: n.is_read ? 600 : 800, margin:0, lineHeight:1.5 }}>{n.message}</p>
                <span style={{ color:'#94a3b8', fontSize:10, fontWeight:600 }}>{new Date(n.created_at).toLocaleString('ar-SA', { dateStyle:'short', timeStyle:'short' })}</span>
              </div>
              {!n.is_read && <div style={{ width:8, height:8, background:'#3b82f6', borderRadius:'50%', flexShrink:0, marginTop:4 }} />}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   SMALL COMPONENTS & HELPERS
══════════════════════════════════════════════════════════════ */
function ToastList({ toasts }: { toasts: Toast[] }) {
  return (
    <div style={{ position:'fixed', bottom:24, left:'50%', transform:'translateX(-50%)', zIndex:9999, display:'flex', flexDirection:'column', gap:8, alignItems:'center' }}>
      {toasts.map(t => (
        <div key={t.id} style={{ background: t.type==='success'?'#059669':t.type==='error'?'#dc2626':'#2563eb', color:'#fff', borderRadius:12, padding:'10px 20px', fontWeight:800, fontSize:13, boxShadow:'0 8px 24px rgba(0,0,0,0.2)', animation:'fadeInUp 0.3s ease' }}>
          {t.msg}
        </div>
      ))}
    </div>
  );
}

function HdrBtn({ onClick, title, children }: { onClick: () => void; title: string; children: React.ReactNode }) {
  return (
    <button onClick={onClick} title={title} style={{ position:'relative', width:38, height:38, background:'rgba(255,255,255,0.08)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:10, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', color:'#cbd5e1' }}>
      {children}
    </button>
  );
}

function NotifDot({ count }: { count: number }) {
  return <span style={{ position:'absolute', top:5, right:5, width:16, height:16, background:'#ef4444', borderRadius:'50%', fontSize:9, fontWeight:900, color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', border:'2px solid #0f172a' }}>{count > 9 ? '9+' : count}</span>;
}

function SearchInput({ value, onChange, darkMode, placeholder = 'بحث...' }: { value: string; onChange: (v: string) => void; darkMode: boolean; placeholder?: string }) {
  return (
    <div style={{ position:'relative' }}>
      <span style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)', fontSize:13, color:'#94a3b8' }}>🔍</span>
      <input type="search" placeholder={placeholder} value={value} onChange={e => onChange(e.target.value)}
        style={{ padding:'8px 32px 8px 12px', borderRadius:10, border:`1px solid ${darkMode ? '#334155' : '#e2e8f0'}`, background: darkMode ? '#1e293b' : '#fff', color: darkMode ? '#f1f5f9' : '#1e293b', fontSize:13, fontWeight:700, fontFamily:"'Tajawal',sans-serif", outline:'none', width:180 }} />
    </div>
  );
}

function StatusSelect({ value, onChange, darkMode }: { value: string; onChange: (v: string) => void; darkMode: boolean }) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)}
      style={{ padding:'8px 12px', borderRadius:10, border:`1px solid ${darkMode ? '#334155' : '#e2e8f0'}`, background: darkMode ? '#1e293b' : '#fff', color: darkMode ? '#f1f5f9' : '#1e293b', fontSize:13, fontWeight:700, fontFamily:"'Tajawal',sans-serif", outline:'none' }}>
      <option value="">جميع الحالات</option>
      {['مفتوح','مصعد','موجه','طلب RCA','مغلق','RCA Completed'].map(s => <option key={s} value={s}>{s}</option>)}
    </select>
  );
}

function Pagination({ page, total, onChange, darkMode }: { page: number; total: number; onChange: (p: number) => void; darkMode: boolean }) {
  if (total <= 1) return null;
  return (
    <div style={{ display:'flex', justifyContent:'center', alignItems:'center', gap:10 }}>
      <button disabled={page <= 1} onClick={() => onChange(page - 1)} style={{ ...btnSecondary(darkMode), opacity: page <= 1 ? 0.4 : 1 }}>＜ السابق</button>
      <span style={{ fontWeight:700, fontSize:13, color:'#94a3b8' }}>صفحة {page} من {total}</span>
      <button disabled={page >= total} onClick={() => onChange(page + 1)} style={{ ...btnSecondary(darkMode), opacity: page >= total ? 0.4 : 1 }}>التالي ＞</button>
    </div>
  );
}

function SkeletonGrid() {
  return (
    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(320px,1fr))', gap:16 }}>
      {[1,2,3,4].map(i => <div key={i} style={{ height:200, borderRadius:20, background:'linear-gradient(90deg,#e2e8f0 25%,#f1f5f9 50%,#e2e8f0 75%)', backgroundSize:'200% 100%', animation:'shimmer 1.5s ease-in-out infinite' }} />)}
    </div>
  );
}

function EmptyBox({ msg, darkMode }: { msg: string; darkMode: boolean }) {
  return (
    <div style={{ background: darkMode ? '#0f172a' : '#fff', borderRadius:20, padding:'50px 40px', textAlign:'center', border:`2px dashed ${darkMode ? '#334155' : '#e2e8f0'}` }}>
      <div style={{ fontSize:48, marginBottom:10 }}>📭</div>
      <div style={{ color:'#94a3b8', fontWeight:700 }}>{msg}</div>
    </div>
  );
}

function FieldLabel({ label, required }: { label: string; required?: boolean }) {
  return <label style={{ display:'block', marginBottom:6, color:'#64748b', fontSize:12, fontWeight:800 }}>{label} {required && <span style={{ color:'#ef4444' }}>*</span>}</label>;
}

function ErrMsg({ msg }: { msg?: string }) {
  return <p style={{ color:'#ef4444', fontSize:11, fontWeight:700, marginTop:4 }}>{msg}</p>;
}

/* ══════════════════════════════════════════════════════════════
   RCA VIEW MODAL
══════════════════════════════════════════════════════════════ */
function RcaViewModal({ incidentId, darkMode, onClose }: {
  incidentId: string; darkMode: boolean; onClose: () => void;
}) {
  const bg    = darkMode ? '#0f172a' : '#fff';
  const bgSub = darkMode ? '#1e293b' : '#f8fafc';

  const { data, isLoading } = useQuery({
    queryKey: ['rca_view', incidentId],
    queryFn: async () => {
      const [incRes, rcaRes] = await Promise.all([
        supabase.from('incidents').select('*').eq('id', incidentId).single(),
        supabase.from('rca_reports').select('*').eq('incident_id', incidentId).maybeSingle(),
      ]);
      return { incident: incRes.data, rca: rcaRes.data };
    },
  });

  const WHY_QS = [
    'ما الذي حدث؟ ولماذا حدث؟',
    'ما العوامل المساهمة التي أدت إلى ذلك؟',
    'لماذا لم يتم اكتشاف أو منع هذه العوامل؟',
    'ما الخلل النظامي أو التنظيمي الذي سمح بحدوث ذلك؟',
    'ما هو السبب الجذري النهائي؟',
  ];

  const handlePrint = () => {
    if (!data) return;
    const { incident: ev, rca } = data;
    const riskColors: Record<string,string> = { حرج:'#b91c1c', عالي:'#c2410c', مرتفع:'#c2410c', متوسط:'#b45309', منخفض:'#15803d' };
    const riskColor = riskColors[(ev as any)?.risk_level] || '#0f172a';
    const evDate    = ev ? new Date((ev as any).created_at).toLocaleDateString('ar-SA', { year:'numeric', month:'long', day:'numeric' }) : '—';
    const deadline  = rca?.deadline ? new Date(rca.deadline).toLocaleDateString('ar-SA', { year:'numeric', month:'long', day:'numeric' }) : '—';
    const printDate = new Date().toLocaleDateString('ar-SA', { year:'numeric', month:'long', day:'numeric', hour:'2-digit', minute:'2-digit' } as any);
    const whyVals   = rca ? [rca.why_1, rca.why_2, rca.why_3, rca.why_4, rca.root_cause] : [];

    const F = "font-family:'Tajawal',Arial,sans-serif;";
    const CSS = `
      @import url('https://fonts.googleapis.com/css2?family=Tajawal:wght@400;700;900&display=swap');
      *{${F}box-sizing:border-box;margin:0;padding:0;}
      body{background:#f5f5f5;color:#333;font-size:13px;direction:rtl;}
      .wrap{max-width:800px;margin:0 auto;padding:24px 16px;}
      .card{background:#fff;border:1px solid #e2e8f0;border-radius:20px;overflow:hidden;box-shadow:0 10px 30px rgba(0,0,0,0.05);}
      .brand-hdr{background:linear-gradient(135deg,#0f172a,#1e1b4b,#2e1065);padding:22px 32px;text-align:center;}
      .brand-name{font-size:22px;font-weight:900;color:#fff;}
      .report-sub{font-size:12px;font-weight:700;color:#c4b5fd;margin-top:5px;}
      .body{padding:24px 32px;}
      .sec-hdr{font-size:13px;font-weight:900;color:#fff;padding:10px 16px;border-radius:12px 12px 0 0;}
      .purple{background:linear-gradient(90deg,#1e1b4b,#2e1065);}
      .green{background:linear-gradient(90deg,#065f46,#047857);}
      .mb20{margin-bottom:20px;}
      table.dt{width:100%;border-collapse:collapse;border:1px solid #e2e8f0;border-radius:0 0 12px 12px;overflow:hidden;}
      table.dt td{padding:11px 14px;font-size:13px;border-bottom:1px solid #e2e8f0;text-align:right;}
      table.dt td.lbl{font-weight:bold;color:#64748b;background:#f8fafc;width:33%;border-left:1px solid #e2e8f0;}
      table.dt td.val{font-weight:900;color:#0f172a;}
      table.dt tr:last-child td{border-bottom:none;}
      .desc-row{padding:12px 16px;background:#f8fafc;border:1px solid #e2e8f0;border-top:none;border-radius:0 0 12px 12px;}
      .why-box{border:1px solid #e2e8f0;border-top:none;border-radius:0 0 12px 12px;overflow:hidden;}
      .why-row{padding:12px 16px;border-bottom:1px solid #f1f5f9;}
      .why-row:last-child{border-bottom:none;}
      .why-q{font-size:11px;font-weight:700;color:#6d28d9;margin-bottom:4px;}
      .why-a{font-size:13px;font-weight:900;color:#0f172a;line-height:1.6;}
      .root .why-q{color:#b91c1c;} .root .why-a{color:#b91c1c;}
      .capa-box{border:1px solid #e2e8f0;border-top:none;border-radius:0 0 12px 12px;overflow:hidden;}
      .capa-action{padding:14px 16px;background:#f0fdf4;border-bottom:1px solid #d1fae5;}
      .footer{padding:14px 32px;background:#f8fafc;border-top:1px solid #e2e8f0;text-align:center;}
      .footer p{font-size:11px;color:#64748b;font-weight:bold;}
      *{-webkit-print-color-adjust:exact!important;print-color-adjust:exact!important;}
      @media print{body{background:#fff;}.wrap{padding:0;max-width:100%;}@page{margin:1.5cm;size:A4;}}
    `;

    const H = `<!DOCTYPE html><html dir="rtl" lang="ar"><head><meta charset="UTF-8">
      <title>تقرير RCA — ${(ev as any)?.event_id ?? ''}</title>
      <style>${CSS}</style></head><body><div class="wrap"><div class="card">
      <div class="brand-hdr"><div class="brand-name">نظام مُناوِب | munawb</div>
      <div class="report-sub">تقرير تحليل السبب الجذري والخطة التصحيحية والوقائية (CAPA)</div></div>
      <div class="body">
        <div class="mb20">
          <div class="sec-hdr purple">معلومات الحدث</div>
          <table class="dt">
            <tr><td class="lbl">رقم الحدث</td><td class="val" dir="ltr" style="font-family:monospace;color:#1d4ed8;">${(ev as any)?.event_id ?? '—'}</td></tr>
            <tr><td class="lbl">القسم / الموقع</td><td class="val">${(ev as any)?.location ?? '—'}</td></tr>
            <tr><td class="lbl">مستوى الخطورة</td><td class="val" style="color:${riskColor}">${(ev as any)?.risk_level ?? '—'}</td></tr>
            <tr><td class="lbl">تاريخ الحدث</td><td class="val">${evDate}</td></tr>
          </table>
          ${(ev as any)?.description ? `<div class="desc-row"><div style="font-size:11px;font-weight:700;color:#64748b;margin-bottom:3px;">وصف الحدث</div><div style="font-size:13px;font-weight:700;line-height:1.6;">${(ev as any).description}</div></div>` : ''}
        </div>
        ${rca ? `
        <div class="mb20">
          <div class="sec-hdr purple">تحليل الأسباب الجذرية — منهجية الأسباب الخمسة</div>
          <div class="why-box">
            ${WHY_QS.map((q, i) => {
              const ans = (whyVals[i] ?? '').toString().trim();
              if (!ans) return '';
              return `<div class="why-row${i===4?' root':''}"><div class="why-q">س${i+1}: ${q}</div><div class="why-a">${ans}</div></div>`;
            }).join('')}
          </div>
        </div>
        <div class="mb20">
          <div class="sec-hdr green">خطة العمل التصحيحية والوقائية (CAPA Plan)</div>
          <div class="capa-box">
            ${rca.action_plan ? `<div class="capa-action"><div style="font-size:11px;font-weight:700;color:#065f46;margin-bottom:3px;">الإجراء التصحيحي</div><div style="font-size:13px;font-weight:900;line-height:1.6;">${rca.action_plan}</div></div>` : ''}
            <table class="dt" style="border-radius:0;">
              <tr><td class="lbl">المسؤول عن التنفيذ</td><td class="val">${rca.owner ?? '—'}</td></tr>
              <tr><td class="lbl">الموعد النهائي</td><td class="val">${deadline}</td></tr>
            </table>
          </div>
        </div>` : ''}
      </div>
      <div class="footer"><p>تقرير رسمي صادر من نظام مُناوِب • تاريخ الطباعة: ${printDate}</p></div>
    </div></div></body></html>`;

    const w = window.open('', '_blank');
    if (w) { w.document.write(H); w.document.close(); setTimeout(() => { w.focus(); w.print(); }, 500); }
  };

  return (
    <div onClick={onClose} style={{ position:'fixed', inset:0, zIndex:200, background:'rgba(15,23,42,0.92)', backdropFilter:'blur(12px)', display:'flex', justifyContent:'center', alignItems:'center', padding:16 }}>
      <div onClick={e => e.stopPropagation()} style={{
        background:bg, width:'100%', maxWidth:660, borderRadius:28,
        overflow:'hidden', display:'flex', flexDirection:'column',
        maxHeight:'90vh', animation:'modalIn 0.25s ease',
        border:'1px solid rgba(124,58,237,0.3)',
        boxShadow:'0 24px 64px rgba(0,0,0,0.5)',
      }}>
        {/* Header */}
        <div style={{ background:'linear-gradient(135deg,#2e1065,#4c1d95)', padding:'16px 20px', display:'flex', justifyContent:'space-between', alignItems:'center', flexShrink:0 }}>
          <div style={{ color:'#fff', fontWeight:900, fontSize:15, display:'flex', alignItems:'center', gap:8 }}>
            📋 تقرير تحليل السبب الجذري
            {data?.incident && <span style={{ background:'rgba(255,255,255,0.15)', color:'#c4b5fd', fontFamily:'monospace', fontSize:11, padding:'2px 8px', borderRadius:6 }}>{(data.incident as any).event_id}</span>}
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            <button onClick={handlePrint} title="طباعة التقرير" style={{ background:'rgba(255,255,255,0.15)', border:'1px solid rgba(255,255,255,0.2)', borderRadius:8, width:34, height:34, color:'#fff', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', fontSize:15 }}>🖨️</button>
            <button onClick={onClose} style={{ background:'rgba(255,255,255,0.15)', border:'1px solid rgba(255,255,255,0.2)', borderRadius:8, width:34, height:34, color:'#fff', cursor:'pointer', fontSize:16 }}>✕</button>
          </div>
        </div>

        {isLoading ? (
          <div style={{ padding:60, textAlign:'center', color:'#94a3b8' }}>
            <div style={{ fontSize:32 }}>⏳</div>
            <div style={{ fontWeight:700, marginTop:8 }}>جارٍ تحميل التقرير...</div>
          </div>
        ) : (
          <div style={{ overflowY:'auto', flex:1, padding:20 }}>

            {/* Incident info */}
            <div style={{ background:'rgba(109,40,217,0.08)', border:'1px solid rgba(109,40,217,0.2)', borderRadius:14, padding:16, marginBottom:16 }}>
              <div style={{ color:'#7c3aed', fontSize:10, fontWeight:900, textTransform:'uppercase', letterSpacing:2, marginBottom:10 }}>معلومات الحدث</div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:12 }}>
                {[
                  { l:'رقم الحدث', v:(data?.incident as any)?.event_id ?? '—' },
                  { l:'مستوى الخطورة', v:(data?.incident as any)?.risk_level ?? '—' },
                  { l:'القسم / الموقع', v:(data?.incident as any)?.location ?? '—' },
                  { l:'تاريخ الحدث', v:(data?.incident as any)?.created_at ? new Date((data?.incident as any).created_at).toLocaleDateString('ar-SA') : '—' },
                ].map(d => (
                  <div key={d.l}>
                    <div style={{ color:'#94a3b8', fontSize:10, fontWeight:700, marginBottom:3 }}>{d.l}</div>
                    <div style={{ fontWeight:900, fontSize:13 }}>{d.v}</div>
                  </div>
                ))}
              </div>
              {(data?.incident as any)?.description && (
                <div>
                  <div style={{ color:'#94a3b8', fontSize:10, fontWeight:700, marginBottom:4 }}>وصف الحدث</div>
                  <p style={{ fontWeight:700, fontSize:13, lineHeight:1.6, margin:0 }}>{(data?.incident as any)?.description}</p>
                </div>
              )}
            </div>

            {/* RCA section */}
            {!data?.rca ? (
              <div style={{ background:'#fffbeb', border:'1px solid #fde68a', borderRadius:14, padding:20, textAlign:'center' }}>
                <div style={{ fontSize:32, marginBottom:8 }}>⚠️</div>
                <div style={{ color:'#92400e', fontWeight:800, fontSize:13 }}>لم يتم العثور على تحليل RCA مسجّل لهذا الحدث.</div>
              </div>
            ) : (
              <>
                {/* 5 Whys */}
                <div style={{ background: darkMode ? '#1e293b' : '#fff', border:`1px solid ${darkMode ? '#334155' : '#e2e8f0'}`, borderRadius:14, overflow:'hidden', marginBottom:14 }}>
                  <div style={{ background:'linear-gradient(90deg,#1e1b4b,#2e1065)', padding:'10px 16px' }}>
                    <span style={{ color:'#fff', fontWeight:900, fontSize:13 }}>🔬 تحليل الأسباب الجذرية — أسلوب الأسباب الخمسة</span>
                  </div>
                  {WHY_QS.map((q, i) => {
                    const vals = data.rca ? [data.rca.why_1, data.rca.why_2, data.rca.why_3, data.rca.why_4, data.rca.root_cause] : [];
                    const ans  = vals[i];
                    if (!ans) return null;
                    const isRoot = i === 4;
                    return (
                      <div key={i} style={{ padding:'12px 16px', borderBottom: i < 4 ? `1px solid ${darkMode ? '#334155' : '#f1f5f9'}` : 'none' }}>
                        <div style={{ color: isRoot ? '#b91c1c' : '#7c3aed', fontSize:10, fontWeight:800, marginBottom:4 }}>س{i+1}: {q}</div>
                        <div style={{ color: isRoot ? '#b91c1c' : (darkMode ? '#e2e8f0' : '#1e293b'), fontWeight:900, fontSize:13, lineHeight:1.6 }}>{ans}</div>
                      </div>
                    );
                  })}
                </div>

                {/* CAPA Plan */}
                <div style={{ background: darkMode ? '#1e293b' : '#fff', border:`1px solid ${darkMode ? '#334155' : '#e2e8f0'}`, borderRadius:14, overflow:'hidden' }}>
                  <div style={{ background:'linear-gradient(90deg,#065f46,#047857)', padding:'10px 16px' }}>
                    <span style={{ color:'#fff', fontWeight:900, fontSize:13 }}>📋 خطة العمل التصحيحية والوقائية (CAPA Plan)</span>
                  </div>
                  <div style={{ padding:16 }}>
                    {data.rca.action_plan && (
                      <div style={{ background: darkMode ? '#0f172a' : '#f0fdf4', border:'1px solid #bbf7d0', borderRadius:10, padding:12, marginBottom:12 }}>
                        <div style={{ color:'#065f46', fontSize:10, fontWeight:800, marginBottom:4 }}>الإجراء التصحيحي</div>
                        <p style={{ fontWeight:800, fontSize:13, lineHeight:1.6, margin:0 }}>{data.rca.action_plan}</p>
                      </div>
                    )}
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                      <div>
                        <div style={{ color:'#94a3b8', fontSize:10, fontWeight:700, marginBottom:3 }}>المسؤول عن التنفيذ</div>
                        <div style={{ fontWeight:900, fontSize:13 }}>{data.rca.owner ?? '—'}</div>
                      </div>
                      <div>
                        <div style={{ color:'#94a3b8', fontSize:10, fontWeight:700, marginBottom:3 }}>الموعد النهائي</div>
                        <div style={{ fontWeight:900, fontSize:13 }}>{data.rca.deadline ? new Date(data.rca.deadline).toLocaleDateString('ar-SA') : '—'}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   PROFILE MODAL
══════════════════════════════════════════════════════════════ */
function ProfileModal({ profile, darkMode, onClose, onSuccess, onError }: {
  profile: any; darkMode: boolean;
  onClose: () => void; onSuccess: (msg: string) => void; onError: (msg: string) => void;
}) {
  const bg    = darkMode ? '#0f172a' : '#fff';
  const bgSub = darkMode ? '#1e293b' : '#f8fafc';

  const [phone, setPhone]           = useState<string>((profile as any)?.phone ?? '');
  const [oldPass, setOldPass]       = useState('');
  const [newPass, setNewPass]       = useState('');
  const [confPass, setConfPass]     = useState('');
  const [showOld, setShowOld]       = useState(false);
  const [showNew, setShowNew]       = useState(false);
  const [saving, setSaving]         = useState(false);
  const [notifEmail, setNotifEmail] = useState(true);
  const [notifWa, setNotifWa]       = useState(true);
  const passError = newPass && confPass && newPass !== confPass;

  const handleSave = async () => {
    if (newPass && !oldPass) { onError('يجب إدخال كلمة المرور الحالية أولاً.'); return; }
    if (passError) { onError('كلمتا المرور الجديدتان غير متطابقتين!'); return; }
    setSaving(true);
    try {
      const { error: profileErr } = await supabase
        .from('profiles')
        .update({ phone, notif_email: notifEmail, notif_wa: notifWa } as any)
        .eq('id', profile?.id);
      if (profileErr) throw profileErr;
      if (newPass) {
        const { error: passErr } = await supabase.auth.updateUser({ password: newPass });
        if (passErr) throw passErr;
      }
      onSuccess('تم حفظ تحديثات الملف الشخصي بنجاح ✅');
    } catch (e: any) {
      onError(e?.message ?? 'حدث خطأ أثناء الحفظ');
    } finally { setSaving(false); }
  };

  const inputSt: React.CSSProperties = {
    width:'100%', padding:'10px 12px', borderRadius:10,
    border:`1px solid ${darkMode ? '#334155' : '#e2e8f0'}`,
    background: darkMode ? '#0f172a' : '#fff',
    color: darkMode ? '#f1f5f9' : '#1e293b',
    fontSize:13, fontWeight:700,
    fontFamily:"'Tajawal',sans-serif", outline:'none', boxSizing:'border-box',
  };
  const disabledSt: React.CSSProperties = { ...inputSt, background: darkMode ? '#1e293b' : '#f1f5f9', color:'#94a3b8', cursor:'not-allowed' };
  const lbl = (t: string) => <div style={{ color:'#64748b', fontSize:11, fontWeight:800, marginBottom:5 }}>{t}</div>;

  return (
    <div onClick={onClose} style={{ position:'fixed', inset:0, zIndex:70, background:'rgba(15,23,42,0.65)', backdropFilter:'blur(6px)', display:'flex', justifyContent:'center', alignItems:'center', padding:16 }}>
      <div onClick={e => e.stopPropagation()} style={{
        background:bg, width:'100%', maxWidth:520, borderRadius:24,
        overflow:'hidden', display:'flex', flexDirection:'column',
        maxHeight:'90vh', animation:'modalIn 0.25s ease',
        border:`1px solid ${darkMode ? '#334155' : '#e2e8f0'}`,
        boxShadow:'0 24px 64px rgba(0,0,0,0.3)',
      }}>
        {/* Header */}
        <div style={{ background:'linear-gradient(135deg,#1d4ed8,#4f46e5)', padding:'16px 20px', display:'flex', justifyContent:'space-between', alignItems:'center', flexShrink:0 }}>
          <div style={{ color:'#fff', fontWeight:900, fontSize:15 }}>⚙️ إعدادات الملف الشخصي</div>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'rgba(255,255,255,0.7)', fontSize:20, cursor:'pointer' }}>✕</button>
        </div>

        <div style={{ overflowY:'auto', flex:1, padding:20 }}>

          {/* User info (read-only) */}
          <div style={{ background:bgSub, borderRadius:14, padding:16, marginBottom:16, border:`1px solid ${darkMode ? '#334155' : '#e2e8f0'}` }}>
            <div style={{ color:'#3b82f6', fontSize:11, fontWeight:900, marginBottom:12, textTransform:'uppercase', letterSpacing:1 }}>👤 بيانات المستخدم</div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:12 }}>
              <div>{lbl('الاسم الكامل')}<input readOnly value={profile?.name ?? ''} style={disabledSt} /></div>
              <div>{lbl('الإدارة / القسم')}<input readOnly value={(profile as any)?.department?.name ?? ''} style={disabledSt} /></div>
            </div>
            <div style={{ marginBottom:12 }}>
              {lbl('البريد الإلكتروني')}
              <input readOnly value={profile?.email ?? ''} dir="ltr" style={disabledSt} />
            </div>
            <div>
              {lbl('رقم الجوال')}
              <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                <span style={{ color:'#94a3b8', fontSize:13, fontWeight:700 }}>+966</span>
                <input
                  type="tel" value={phone} onChange={e => setPhone(e.target.value)}
                  placeholder="5xxxxxxxx" dir="ltr"
                  style={{ ...inputSt, flex:1 }}
                />
              </div>
            </div>
          </div>

          {/* Password */}
          <div style={{ background:bgSub, borderRadius:14, padding:16, marginBottom:16, border:`1px solid ${darkMode ? '#334155' : '#e2e8f0'}` }}>
            <div style={{ color:'#3b82f6', fontSize:11, fontWeight:900, marginBottom:12, textTransform:'uppercase', letterSpacing:1 }}>🔒 إعدادات الأمان</div>
            <p style={{ color:'#94a3b8', fontSize:10, fontWeight:700, marginBottom:12 }}>اترك حقول كلمة المرور فارغة إذا لم ترغب في تغييرها.</p>
            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              <div>
                {lbl('كلمة المرور الحالية')}
                <div style={{ position:'relative' }}>
                  <input type={showOld ? 'text' : 'password'} value={oldPass} onChange={e => setOldPass(e.target.value)} placeholder="••••••••" style={{ ...inputSt, paddingLeft:36 }} />
                  <button onClick={() => setShowOld(v => !v)} style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', color:'#94a3b8', fontSize:14 }}>
                    {showOld ? '🙈' : '👁️'}
                  </button>
                </div>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                <div>
                  {lbl('كلمة المرور الجديدة')}
                  <div style={{ position:'relative' }}>
                    <input type={showNew ? 'text' : 'password'} value={newPass} onChange={e => setNewPass(e.target.value)} placeholder="••••••••" style={{ ...inputSt, paddingLeft:36 }} />
                    <button onClick={() => setShowNew(v => !v)} style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', color:'#94a3b8', fontSize:14 }}>
                      {showNew ? '🙈' : '👁️'}
                    </button>
                  </div>
                </div>
                <div>
                  {lbl('تأكيد كلمة المرور')}
                  <input type="password" value={confPass} onChange={e => setConfPass(e.target.value)} placeholder="••••••••" style={{ ...inputSt, border: passError ? '1px solid #ef4444' : (inputSt.border as string) }} />
                </div>
              </div>
              {passError && <p style={{ color:'#ef4444', fontSize:11, fontWeight:700 }}>⚠️ كلمتا المرور الجديدتان غير متطابقتين!</p>}
            </div>
          </div>

          {/* Notification prefs */}
          <div style={{ background:bgSub, borderRadius:14, padding:16, marginBottom:20, border:`1px solid ${darkMode ? '#334155' : '#e2e8f0'}` }}>
            <div style={{ color:'#3b82f6', fontSize:11, fontWeight:900, marginBottom:12, textTransform:'uppercase', letterSpacing:1 }}>🔔 تفضيلات الإشعارات</div>
            {[
              { label:'إشعارات البريد الإلكتروني', sub:'استلام تحديثات البلاغات على البريد', val:notifEmail, set:setNotifEmail },
              { label:'إشعارات واتساب (WhatsApp)', sub:'التنبيهات العاجلة للأحداث المصعدة', val:notifWa, set:setNotifWa },
            ].map(item => (
              <div key={item.label} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', paddingBottom:12, marginBottom:12, borderBottom:`1px solid ${darkMode ? '#334155' : '#f1f5f9'}` }}>
                <div>
                  <div style={{ fontWeight:800, fontSize:13 }}>{item.label}</div>
                  <div style={{ color:'#94a3b8', fontSize:10, fontWeight:600, marginTop:2 }}>{item.sub}</div>
                </div>
                <button
                  onClick={() => item.set((v: boolean) => !v)}
                  style={{
                    width:44, height:24, borderRadius:12, border:'none', cursor:'pointer',
                    background: item.val ? '#3b82f6' : '#cbd5e1',
                    position:'relative', transition:'background 0.2s', flexShrink:0,
                  }}
                >
                  <span style={{
                    position:'absolute', top:2, width:20, height:20, background:'#fff', borderRadius:'50%',
                    transition:'left 0.2s', left: item.val ? 22 : 2,
                    boxShadow:'0 1px 4px rgba(0,0,0,0.2)',
                  }} />
                </button>
              </div>
            ))}
          </div>

          {/* Buttons */}
          <div style={{ display:'flex', gap:10 }}>
            <button onClick={onClose} style={{ flex:1, background: darkMode ? '#334155' : '#f1f5f9', border:'none', borderRadius:10, padding:'12px', color: darkMode ? '#94a3b8' : '#64748b', fontWeight:900, fontSize:13, cursor:'pointer', fontFamily:"'Tajawal',sans-serif" }}>
              إلغاء
            </button>
            <button onClick={handleSave} disabled={saving || !!passError} style={{
              flex:2, background: saving || passError ? '#94a3b8' : '#2563eb',
              border:'none', borderRadius:10, padding:'12px', color:'#fff',
              fontWeight:900, fontSize:13, cursor: saving || passError ? 'not-allowed' : 'pointer',
              fontFamily:"'Tajawal',sans-serif", display:'flex', alignItems:'center', justifyContent:'center', gap:8,
            }}>
              {saving ? '⏳ جارٍ الحفظ...' : '💾 حفظ التحديثات'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   CALL MODAL — نظام الاستدعاء الطارئ
══════════════════════════════════════════════════════════════ */
function CallModal({ profile, departments, darkMode, onClose, onSuccess, onError }: {
  profile: any; departments: { id:string; name:string }[]; darkMode: boolean;
  onClose: () => void; onSuccess: (msg: string) => void; onError: (msg: string) => void;
}) {
  const bg    = darkMode ? '#0f172a' : '#fff';
  const bgSub = darkMode ? '#1e293b' : '#f8fafc';

  const [selectedCode, setSelectedCode] = useState<string | null>(null);
  const [callType, setCallType]         = useState('');
  const [priority, setPriority]         = useState('');
  const [location, setLocation]         = useState((profile as any)?.department?.name ?? '');
  const [details, setDetails]           = useState('');
  const [notifyWa, setNotifyWa]         = useState(true);
  const [notifyExec, setNotifyExec]     = useState(false);
  const [submitting, setSubmitting]     = useState(false);
  const [callLog, setCallLog]           = useState<{ label:string; loc:string; time:string }[]>([]);
  const [timerSecs, setTimerSecs]       = useState(0);
  const [timerRunning, setTimerRunning] = useState(false);

  useEffect(() => {
    if (!timerRunning) return;
    const iv = setInterval(() => setTimerSecs(s => s + 1), 1000);
    return () => clearInterval(iv);
  }, [timerRunning]);

  const fmtTimer = (s: number) => `${String(Math.floor(s/60)).padStart(2,'0')}:${String(s%60).padStart(2,'0')}`;

  const selectCode = (code: typeof QUICK_CODES[0]) => {
    setSelectedCode(code.code);
    if (code.code !== 'CODE_CUSTOM') {
      setCallType(code.label);
      setPriority(code.priority);
      setDetails(code.desc);
    } else {
      setCallType('');
      setPriority('');
      setDetails('');
    }
  };

  const handleSubmit = async () => {
    if (!callType) { onError('يرجى اختيار نوع الاستدعاء أو الجهة المطلوبة.'); return; }
    if (!priority) { onError('يرجى تحديد مستوى الأهمية.'); return; }
    if (!location) { onError('يرجى تحديد الموقع بدقة.'); return; }
    setSubmitting(true);
    try {
      await supabase.from('incidents').insert({
        event_type:   `[استدعاء] ${callType}`,
        location,
        description:  `${priority} — ${details || callType}\n📢 المُبلِّغ: ${profile?.name ?? ''}${notifyExec ? '\n🔺 تصعيد للإدارة' : ''}`,
        risk_level:   priority === 'حرج جداً (فوراً)' ? 'حرج' : priority === 'عاجل (خلال 15 دقيقة)' ? 'عالي' : 'متوسط',
        risk_score:   priority === 'حرج جداً (فوراً)' ? 25 : priority === 'عاجل (خلال 15 دقيقة)' ? 12 : 6,
        reporter_id:  profile?.id ?? null,
        reporter_name: profile?.name ?? '',
        is_repeated:  false,
        likelihood:   5,
        impact:       5,
      });

      if (notifyWa) {
        const emoji = QUICK_CODES.find(c => c.code === selectedCode)?.icon ?? '🚨';
        const msg = [
          `${emoji} *${callType} — استدعاء طارئ* ${emoji}`,
          '━━━━━━━━━━━━━━━━━━━━',
          `📍 *الموقع:* ${location}`,
          `⚡ *مستوى الأهمية:* ${priority}`,
          details ? `📋 *التفاصيل:* ${details}` : '',
          '━━━━━━━━━━━━━━━━━━━━',
          `👤 *المُبلِّغ:* ${profile?.name ?? ''}`,
          `🕒 ${new Date().toLocaleTimeString('ar-SA', { hour:'2-digit', minute:'2-digit' })}`,
        ].filter(Boolean).join('\n');
        window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
      }

      setTimerRunning(true);
      setCallLog(prev => [{ label: callType, loc: location, time: new Date().toLocaleTimeString('ar-SA', { hour:'2-digit', minute:'2-digit' }) }, ...prev]);
      setCallType(''); setPriority(''); setDetails(''); setSelectedCode(null);
      onSuccess('تم إطلاق نداء الاستدعاء بنجاح! 🚨');
    } catch (e: any) {
      onError(e?.message ?? 'تعذّر إرسال الاستدعاء');
    } finally { setSubmitting(false); }
  };

  const selectSt: React.CSSProperties = {
    width:'100%', padding:'10px 12px', borderRadius:10,
    border:`1px solid ${darkMode ? '#334155' : '#e2e8f0'}`,
    background: darkMode ? '#0f172a' : '#fff',
    color: darkMode ? '#f1f5f9' : '#1e293b',
    fontSize:13, fontWeight:700, fontFamily:"'Tajawal',sans-serif", outline:'none',
  };
  const textareaSt: React.CSSProperties = { ...selectSt, resize:'none' };
  const lbl = (t: string) => <div style={{ color:'#64748b', fontSize:11, fontWeight:800, marginBottom:5 }}>{t}</div>;

  return (
    <div onClick={onClose} style={{ position:'fixed', inset:0, zIndex:80, background:'rgba(15,23,42,0.88)', backdropFilter:'blur(10px)', display:'flex', justifyContent:'center', alignItems:'center', padding:16 }}>
      <div onClick={e => e.stopPropagation()} style={{
        background:bg, width:'100%', maxWidth:600, borderRadius:28,
        overflow:'hidden', display:'flex', flexDirection:'column',
        maxHeight:'94vh', animation:'modalIn 0.25s ease',
        border:'1px solid rgba(239,68,68,0.2)',
        boxShadow:'0 24px 64px rgba(0,0,0,0.5)',
      }}>
        {/* Header */}
        <div style={{ background:'linear-gradient(135deg,#1a0505,#3b0a0a,#1e1b4b)', padding:'16px 20px', flexShrink:0 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <div style={{ display:'flex', alignItems:'center', gap:12 }}>
              <div style={{ width:42, height:42, background:'rgba(239,68,68,0.3)', border:'1px solid rgba(239,68,68,0.4)', borderRadius:12, display:'flex', alignItems:'center', justifyContent:'center', fontSize:20, animation:'emergencyPulse 1.6s ease-in-out infinite' }}>
                📢
              </div>
              <div>
                <div style={{ color:'#fff', fontWeight:900, fontSize:15, display:'flex', alignItems:'center', gap:8 }}>
                  نظام الاستدعاء والإعلان الطارئ
                  <span style={{ background:'rgba(239,68,68,0.4)', border:'1px solid rgba(239,68,68,0.5)', color:'#fca5a5', fontSize:9, fontWeight:900, padding:'2px 8px', borderRadius:20, textTransform:'uppercase', letterSpacing:2 }}>LIVE</span>
                </div>
                <div style={{ color:'rgba(252,165,165,0.8)', fontSize:11, fontWeight:700, marginTop:2 }}>تنبيه الفرق وتنسيق الاستجابة الطارئة</div>
              </div>
            </div>
            <button onClick={onClose} style={{ background:'rgba(255,255,255,0.15)', border:'1px solid rgba(255,255,255,0.2)', borderRadius:8, width:34, height:34, color:'#fff', cursor:'pointer', fontSize:16 }}>✕</button>
          </div>
        </div>

        {/* Timer bar */}
        {timerRunning && (
          <div style={{ background:'#dc2626', padding:'8px 20px', display:'flex', justifyContent:'space-between', alignItems:'center', flexShrink:0 }}>
            <div style={{ display:'flex', alignItems:'center', gap:8, color:'#fff', fontWeight:900, fontSize:13 }}>
              ⏱ وقت الاستدعاء: <span style={{ fontFamily:'monospace', fontSize:16 }}>{fmtTimer(timerSecs)}</span>
            </div>
            <div style={{ width:140, height:4, background:'rgba(255,255,255,0.2)', borderRadius:2, overflow:'hidden' }}>
              <div style={{ width:`${Math.min(100, (timerSecs/1800)*100)}%`, height:'100%', background:'linear-gradient(90deg,#22c55e,#f59e0b,#ef4444)', transition:'width 1s linear' }} />
            </div>
          </div>
        )}

        <div style={{ overflowY:'auto', flex:1, padding:20 }}>

          {/* Quick codes grid */}
          <div style={{ marginBottom:20 }}>
            <div style={{ color:'#94a3b8', fontSize:10, fontWeight:900, textTransform:'uppercase', letterSpacing:2, marginBottom:12, display:'flex', alignItems:'center', gap:6 }}>
              ⚡ تفعيل سريع — أكواد الطوارئ المعتمدة
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:8 }}>
              {QUICK_CODES.map(code => (
                <button
                  key={code.code}
                  onClick={() => selectCode(code)}
                  style={{
                    padding:'10px 6px', borderRadius:10, border:`2px solid ${selectedCode === code.code ? code.color : 'transparent'}`,
                    background: selectedCode === code.code ? code.bg : (darkMode ? '#1e293b' : '#f8fafc'),
                    color: code.color, cursor:'pointer', fontFamily:"'Tajawal',sans-serif",
                    fontWeight:900, fontSize:11, display:'flex', flexDirection:'column', alignItems:'center', gap:4,
                    transform: selectedCode === code.code ? 'scale(1.05)' : 'scale(1)',
                    transition:'all 0.2s', boxShadow: selectedCode === code.code ? `0 0 0 3px rgba(0,0,0,0.1)` : 'none',
                  }}
                >
                  <span style={{ fontSize:18 }}>{code.icon}</span>
                  {code.label}
                </button>
              ))}
            </div>
          </div>

          {/* Form */}
          <div style={{ display:'flex', flexDirection:'column', gap:14, borderTop:`1px solid ${darkMode ? '#334155' : '#e2e8f0'}`, paddingTop:16 }}>

            {/* Call type + priority */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              <div>
                {lbl('نوع الاستدعاء / الجهة المطلوبة *')}
                <input
                  value={callType} onChange={e => setCallType(e.target.value)}
                  placeholder="مثال: Code Blue، فريق الإنعاش..."
                  style={{ ...selectSt }}
                />
              </div>
              <div>
                {lbl('مستوى الأهمية *')}
                <select value={priority} onChange={e => setPriority(e.target.value)} style={selectSt}>
                  <option value="">حدد مستوى الأهمية...</option>
                  {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
            </div>

            {/* Location */}
            <div>
              {lbl('الموقع بدقة (القسم / الغرفة) *')}
              <select value={location} onChange={e => setLocation(e.target.value)} style={selectSt}>
                <option value="">اختر الموقع...</option>
                {departments.map(d => <option key={d.id} value={d.name}>{d.name}</option>)}
              </select>
            </div>

            {/* Details */}
            <div>
              {lbl('تفاصيل الاستدعاء (اختياري)')}
              <textarea value={details} onChange={e => setDetails(e.target.value)} rows={2}
                placeholder="اكتب أي معلومات إضافية تفيد الفريق المستدعى..."
                style={textareaSt}
              />
            </div>

            {/* Channels */}
            <div style={{ background:bgSub, borderRadius:12, padding:14, border:`1px solid ${darkMode ? '#334155' : '#fee2e2'}` }}>
              <div style={{ color:'#dc2626', fontSize:10, fontWeight:900, textTransform:'uppercase', letterSpacing:1, marginBottom:10 }}>📡 قنوات النشر الإضافية</div>
              <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
                {[
                  { label:'إشعار داخلي (مُناوِب)', icon:'🔔', val:true,       set:()=>{},        color:'#2563eb', disabled:true },
                  { label:'واتساب (قناة الطوارئ)', icon:'💬', val:notifyWa,   set:setNotifyWa,   color:'#25d366', disabled:false },
                  { label:'تصعيد للإدارة',          icon:'👔', val:notifyExec, set:setNotifyExec, color:'#dc2626', disabled:false },
                ].map(ch => (
                  <label key={ch.label} style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 12px', borderRadius:8, border:`1px solid ${ch.val ? ch.color : (darkMode ? '#334155' : '#e2e8f0')}`, background: ch.val ? `${ch.color}15` : 'transparent', cursor: ch.disabled ? 'not-allowed' : 'pointer' }}>
                    <input type="checkbox" checked={ch.val} onChange={() => !ch.disabled && ch.set((v: boolean) => !v)} style={{ accentColor:ch.color, cursor: ch.disabled ? 'not-allowed' : 'pointer' }} />
                    <span style={{ fontSize:11, fontWeight:800, color: ch.val ? ch.color : '#94a3b8' }}>{ch.icon} {ch.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Buttons */}
            <div style={{ display:'flex', gap:10 }}>
              <button onClick={onClose} style={{ flex:1, background: darkMode ? '#334155' : '#f1f5f9', border:'none', borderRadius:10, padding:'12px', color: darkMode ? '#94a3b8' : '#64748b', fontWeight:900, fontSize:13, cursor:'pointer', fontFamily:"'Tajawal',sans-serif" }}>
                إلغاء
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                style={{
                  flex:2, background: submitting ? '#94a3b8' : '#dc2626',
                  border:'none', borderRadius:10, padding:'12px', color:'#fff',
                  fontWeight:900, fontSize:14, cursor: submitting ? 'not-allowed' : 'pointer',
                  fontFamily:"'Tajawal',sans-serif",
                  display:'flex', alignItems:'center', justifyContent:'center', gap:8,
                  boxShadow: submitting ? 'none' : '0 0 0 0 rgba(239,68,68,0.4)',
                  animation: submitting ? 'none' : 'emergencyPulse 1.6s ease-in-out infinite',
                }}
              >
                {submitting ? '⏳ جارٍ الإرسال...' : '📢 إطلاق نداء الاستدعاء فوراً'}
              </button>
            </div>
          </div>

          {/* Session log */}
          {callLog.length > 0 && (
            <div style={{ borderTop:`1px solid ${darkMode ? '#334155' : '#e2e8f0'}`, paddingTop:16, marginTop:16 }}>
              <div style={{ color:'#94a3b8', fontSize:10, fontWeight:900, textTransform:'uppercase', letterSpacing:1, marginBottom:10 }}>📜 سجل الاستدعاءات (هذه الجلسة)</div>
              <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                {callLog.map((log, i) => (
                  <div key={i} style={{ background: darkMode ? '#1e293b' : '#f8fafc', borderRadius:8, padding:'8px 12px', display:'flex', justifyContent:'space-between', alignItems:'center', border:`1px solid ${darkMode ? '#334155' : '#e2e8f0'}` }}>
                    <span style={{ fontWeight:800, fontSize:12 }}>🚨 {log.label} — {log.loc}</span>
                    <span style={{ color:'#94a3b8', fontSize:10, fontWeight:600 }}>{log.time}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   LOGOUT MODAL
══════════════════════════════════════════════════════════════ */
function LogoutModal({ darkMode, onConfirm, onCancel }: {
  darkMode: boolean; onConfirm: () => void; onCancel: () => void;
}) {
  const bg = darkMode ? '#1e293b' : '#fff';
  return (
    <div onClick={onCancel} style={{ position:'fixed', inset:0, zIndex:9999, background:'rgba(15,23,42,0.7)', backdropFilter:'blur(4px)', display:'flex', justifyContent:'center', alignItems:'center', padding:16 }}>
      <div onClick={e => e.stopPropagation()} style={{
        background:bg, borderRadius:20, padding:'28px 24px', width:'100%', maxWidth:360,
        textAlign:'center', boxShadow:'0 24px 64px rgba(0,0,0,0.35)',
        border:`1px solid ${darkMode ? '#334155' : '#e2e8f0'}`,
        animation:'modalIn 0.2s ease',
      }}>
        <div style={{ width:56, height:56, background:'rgba(239,68,68,0.12)', borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 16px', fontSize:26 }}>
          🔴
        </div>
        <div style={{ fontWeight:900, fontSize:18, marginBottom:8 }}>تسجيل الخروج</div>
        <p style={{ color:'#94a3b8', fontSize:13, fontWeight:600, marginBottom:24, lineHeight:1.6 }}>
          هل أنت متأكد من رغبتك في الخروج من نظام مُناوِب؟
        </p>
        <div style={{ display:'flex', gap:10, justifyContent:'center' }}>
          <button onClick={onCancel} style={{ background: darkMode ? '#334155' : '#f1f5f9', border:'none', borderRadius:10, padding:'10px 24px', color: darkMode ? '#94a3b8' : '#64748b', fontWeight:900, fontSize:13, cursor:'pointer', fontFamily:"'Tajawal',sans-serif" }}>
            إلغاء
          </button>
          <button onClick={onConfirm} style={{ background:'#dc2626', border:'none', borderRadius:10, padding:'10px 24px', color:'#fff', fontWeight:900, fontSize:13, cursor:'pointer', fontFamily:"'Tajawal',sans-serif", boxShadow:'0 4px 12px rgba(220,38,38,0.3)' }}>
            🚪 نعم، خروج
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Style helpers ── */
function btnSecondary(darkMode: boolean): React.CSSProperties {
  return { background: darkMode ? '#1e293b' : '#fff', border:`1px solid ${darkMode ? '#334155' : '#e2e8f0'}`, borderRadius:10, padding:'8px 16px', color: darkMode ? '#94a3b8' : '#64748b', fontSize:12, fontWeight:800, cursor:'pointer', fontFamily:"'Tajawal',sans-serif" };
}

function selectStyle(darkMode: boolean): React.CSSProperties {
  return { padding:'10px 12px', borderRadius:10, border:`1px solid ${darkMode ? '#334155' : '#e2e8f0'}`, background: darkMode ? '#1e293b' : '#fff', color: darkMode ? '#f1f5f9' : '#1e293b', fontSize:13, fontWeight:700, fontFamily:"'Tajawal',sans-serif", outline:'none' };
}

/* ── WhatsApp SVG icon (shared) ── */
const waIcon = (
  <svg width="18" height="18" viewBox="0 0 32 32" fill="#25d366">
    <path d="M16 3C8.82 3 3 8.82 3 16c0 2.62.76 5.07 2.08 7.14L3 29l6.05-2.04A13 13 0 0016 29c7.18 0 13-5.82 13-13S23.18 3 16 3zm6.37 17.84c-.27.76-1.57 1.44-2.15 1.53-.56.09-1.27.13-2.05-.13-.47-.15-1.07-.36-1.84-.7-3.23-1.39-5.34-4.68-5.5-4.9-.16-.22-1.3-1.73-1.3-3.3 0-1.57.82-2.35 1.12-2.67.3-.32.65-.4.86-.4h.62c.2 0 .47-.07.73.56.27.65.92 2.24.99 2.4.08.16.13.36.03.57-.1.22-.16.35-.32.54l-.47.55c-.16.16-.33.33-.14.65.18.32.82 1.36 1.77 2.2 1.22 1.08 2.25 1.41 2.57 1.57.32.16.51.13.7-.08.18-.22.77-.9 1-.21.22.7.22 1.32-.05 1.62z"/>
  </svg>
);

/* ── WhatsApp message composers ── */
function shareIncidentWhatsApp(incident: IncidentRow) {
  const msg = [
    `📋 *بلاغ حدث تشغيلي — ${incident.event_id}*`,
    '━━━━━━━━━━━━━━━━━',
    `📍 *الموقع:* ${incident.location}`,
    `⚡ *النوع:* ${incident.event_type}`,
    `⚠️ *مستوى الخطورة:* ${incident.risk_level}`,
    `📊 *الحالة:* ${incident.status}`,
    `📝 *الوصف:*\n${incident.description}`,
    '━━━━━━━━━━━━━━━━━',
    '🏥 نظام مُناوِب',
  ].join('\n');
  window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
}

function shareUpdateWhatsApp(eventId: string, desc: string, actor: string) {
  const msg = [
    `🔄 *تحديث إجراء — ${eventId}*`,
    '━━━━━━━━━━━━━━━━━',
    `📝 ${desc}`,
    `👤 بواسطة: ${actor}`,
    `🕒 ${new Date().toLocaleTimeString('ar-SA', { hour:'2-digit', minute:'2-digit' })}`,
    '━━━━━━━━━━━━━━━━━',
    '🏥 نظام مُناوِب',
  ].join('\n');
  window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
}

/* ── Emergency call codes ── */
const QUICK_CODES = [
  { code:'CODE_BLUE',    label:'Code Blue',       icon:'💙', color:'#1d4ed8', bg:'#eff6ff', desc:'استدعاء فريق الإنعاش القلبي الرئوي',    priority:'حرج جداً (فوراً)' },
  { code:'CODE_RED',     label:'Code Red',        icon:'🔴', color:'#dc2626', bg:'#fef2f2', desc:'حريق أو دخان — استدعاء فريق السلامة',   priority:'حرج جداً (فوراً)' },
  { code:'CODE_PINK',    label:'Code Pink',       icon:'🩷', color:'#db2777', bg:'#fdf2f8', desc:'اختطاف طفل — إغلاق المخارج فوراً',      priority:'حرج جداً (فوراً)' },
  { code:'CODE_YELLOW',  label:'Code Yellow',     icon:'🟡', color:'#d97706', bg:'#fffbeb', desc:'كارثة داخلية أو ارتفاع مفاجئ في الأحمال', priority:'عاجل (خلال 15 دقيقة)' },
  { code:'CODE_ORANGE',  label:'Code Orange',     icon:'🟠', color:'#ea580c', bg:'#fff7ed', desc:'كارثة خارجية MCI — تفعيل خطة الكوارث', priority:'حرج جداً (فوراً)' },
  { code:'CODE_BLACK',   label:'Code Black',      icon:'⚫', color:'#334155', bg:'#f8fafc', desc:'تهديد أمني أو إخلاء طارئ للمبنى',       priority:'حرج جداً (فوراً)' },
  { code:'CODE_CONSULT', label:'استدعاء استشاري', icon:'🟢', color:'#059669', bg:'#f0fdf4', desc:'طلب حضور الاستشاري المناوب',             priority:'عاجل (خلال 15 دقيقة)' },
  { code:'CODE_CUSTOM',  label:'مخصص',            icon:'✏️', color:'#6d28d9', bg:'#f5f3ff', desc:'',                                        priority:'' },
];
const PRIORITIES = ['حرج جداً (فوراً)', 'عاجل (خلال 15 دقيقة)', 'طارئ (خلال ساعة)'];

/* ── Helpers ── */
function computeRiskLevel(score: number): string {
  if (score >= 20) return 'حرج';
  if (score >= 15) return 'مرتفع';
  if (score >= 8)  return 'عالي';
  if (score >= 4)  return 'متوسط';
  return 'منخفض';
}
